/**
 * Block 3 Practice scenarios catalog · B3-SEL-NN / B3-POM-NN / B3-CI-NN.
 * Source: COORDINATION/app_rework_2026-04-29/13-sandbox-roadmap-s07-s18.md (S13-S16) +
 *         per-Sprint topic alignment (feedback_qa_studio_practice_tasks_topic_alignment).
 *
 * Numbering scheme (per Stage A · Genie ack 2026-05-04 Q2=A 2 ranks):
 *   B3-SEL-NN — S13 (Selenium WebDriver basics) + S15 (Playwright equivalent)
 *   B3-POM-NN — S14 (POM patterns · multi-page navigation · data-driven)
 *   B3-CI-NN  — S16 (CI/CD · GH Actions baseline + meta-блок про GitLab/Jenkins · per Q4=D)
 *
 * Voice continuity: ADHD-novice register · single-author voice anchor от Block 1+2 PR-01..PR-33 + B2-* scenarios.
 * Tech complexity ramp natural for конец 3-го месяца обучения (locator strategy, explicit waits taxonomy,
 * POM patterns, CI YAML basics допустимы; advanced framework architecture (Selenium Grid, Docker exec,
 * Allure reports) — НЕ без teach-block).
 *
 * mountStrategy:
 *   'iframe-route'   — Atelier sub-app real route, students target real app DOM
 *   'iframe-srcdoc'  — embedded HTML snippet for focused isolated drill
 *   'inline-stub'    — placeholder · pending Phase 2 iframe Atelier shell
 *
 * `requires` field · per ADR-A7 mock-driver:
 *   'selenium-runner'   — Selenium-compatible mock-driver (S13/S14/S15)
 *   'playwright-runner' — Playwright equivalent (S15 dedicated)
 *   'ci-pipeline'       — `/ci-pipeline` standalone module (S16)
 *   'screenshot-diff'   — `/screenshot-diff` standalone module (S16)
 *
 * W3.2 authoring batch order:
 *   1. B3-SEL-01..03  · S13 first scenarios (proof-of-pattern · customModule per ADR-A6 default)
 *   2. B3-SEL-04..08  · rest of S13 + start S15 Playwright
 *   3. B3-POM-01..06  · S14 POM patterns
 *   4. B3-SEL-09..11  · S15 Playwright completion
 *   5. B3-CI-01..06   · S16 CI/CD + screenshot diff
 *   (Authoring batches surface DSL extension proposals per ADR-A6 threshold ≥3 same pattern.)
 */

import type {
  Scenario,
  SelectorMismatchBug,
  WaitMismatchBug,
} from '@/types';

// ────────────────────────────────────────────────────────────────────────────
// Sprint 13 · Selenium WebDriver basics — B3-SEL-01..08
// ────────────────────────────────────────────────────────────────────────────

// ─── B3-SEL-01 · S13 · Investigate · selector-mismatch (data-testid renamed) ──

const B3_SEL_01_BUG: SelectorMismatchBug = {
  id: 'B1',
  kind: 'selector-mismatch',
  hotspot: '[data-testid="checkout-button"]',
  expectedSelector: '[data-testid="checkout-btn"]',
  actualSelector: '[data-testid="checkout-button"]',
  mismatchReason: 'attribute-renamed',
  authorNote:
    'Разработчик переименовал `data-testid="checkout-btn"` → `checkout-button"` (полное слово вместо сокращения · самодокументируемее) · но не сообщил QA-каналу. Selenium-тест Маши честно искал старый selector и не нашёл (NoSuchElementException). Hotspot = кнопка оплаты с new attribute. Pedagogical hook: «automation tests = живые контракты с DOM · любое изменение selector ломает test моментально · convention "трогаешь data-testid → пингуешь QA" обязательна».',
};

const B3_SEL_01: Scenario = {
  id: 'B3-SEL-01',
  sprint: 13,
  tier: 'required',
  mode: 'investigate',
  rank: 'automation-architect-junior',
  startRoute: '/checkout',
  mountStrategy: 'iframe-route',
  title: 'Selenium тест падает · NoSuchElementException на checkout',
  brief:
    'Маша утром: «У нас регрессия! Тест на checkout сегодня падает в CI: NoSuchElementException. Никакого изменения в test-code не было». Серж: «Открой страницу, глянь DOM. Может разработчик что-то переименовал».',
  taskMd: [
    '**Контекст.** Маша добавила в команду первый Selenium-тест для checkout-flow две недели назад. Тест проходил каждый день в CI. Сегодня внезапно красный: `NoSuchElementException: Unable to locate element: data-testid="checkout-btn"`. Никаких commit-ов в test-code не было. Маша в Slack: «Я ничего не меняла. Что произошло?»',
    '',
    '**Что делать.**',
    '1. Открой страницу `/checkout` в Atelier sandbox-е. Найди визуально кнопку «Оплатить и завершить заказ» внизу формы.',
    '2. Открой DevTools → Elements panel. Кликни на эту кнопку → посмотри какие у неё attributes сейчас.',
    '3. В правой панели уже открыт **test-snippet** (фрагмент Selenium-кода Маши): `driver.findElement(By.cssSelector("[data-testid=\'checkout-btn\']")).click();`',
    '4. Сравни: какой `data-testid` у кнопки в DOM сейчас vs какой ищет тест?',
    '5. Кликни по кнопке в Atelier-странице — это твой ответ (Selenium бы тоже её ткнул, если бы знал правильный selector).',
    '',
    '**Что важно.** Это первый automation-bug в курсе · и он не «в коде теста». Разработчик переименовал атрибут (`checkout-btn` → `checkout-button`) и не сообщил QA. Тест честно искал старый selector и не нашёл. Это типовая ситуация: automation tests = живые контракты с DOM. Любое изменение `data-testid` ломает тест моментально. Поэтому в реальной команде есть convention: «трогаешь `data-testid` → пингуешь QA-канал». Без такой convention automation suite превращается в красный шум.',
  ].join('\n'),
  bugs: [B3_SEL_01_BUG],
  hints: [
    {
      text: 'Selenium ищет element по `data-testid="checkout-btn"`. Открой DevTools на странице `/checkout`, найди кнопку оплаты, посмотри её attributes. Совпадает ли `data-testid` с тем, что в test-snippet-е?',
    },
    {
      text: 'В DOM кнопка имеет `data-testid="checkout-button"` (с дефисом и полным словом). В test-snippet-е — `checkout-btn` (сокращение). Это два разных selector-а — Selenium не найдёт первый по второму. Кликни на кнопку оплаты в Atelier-странице.',
    },
    {
      text: 'Решение в production: либо договариваешься с разработчиком вернуть старый `data-testid`, либо обновляешь test-snippet к новому имени. В реальности обычно обновляют тест (имя `checkout-button` лучше · самодокументируемое). Кликни кнопку оплаты сейчас — это правильный target.',
    },
  ],
  scoring: { baseScore: 6, firstAttemptBonus: 1, hintPenalty: 1, maxAttempts: 3 },
  achievementTriggers: [
    'detective-junior',
    'sprint-13-required',
    'automation-architect',
  ],
  estimateMin: 18,
  skills: { 'Automation Basics': 4, 'Practice Lab': 5 },
  requires: ['selenium-runner'],
};

// ─── B3-SEL-02 · S13 · Author · write first Selenium script (cart-add flow) ───

const B3_SEL_02: Scenario = {
  id: 'B3-SEL-02',
  sprint: 13,
  tier: 'required',
  mode: 'author',
  rank: 'automation-architect-junior',
  startRoute: '/listings',
  mountStrategy: 'iframe-route',
  title: 'Напиши Selenium-скрипт · добавление работы в корзину',
  brief:
    'Серж: «Хочу видеть сценарий "purchase happy path" в automation suite. Начни с маленького куска — добавление работы в корзину. Mock-driver уже подключён, импорты готовы».',
  taskMd: [
    '**Контекст.** В команде уже есть один Selenium-тест (checkout · который ты только что починил). Серж хочет расширить покрытие — каждый purchase-flow шаг должен быть покрыт отдельным тестом, чтобы при регрессии видеть, *где именно* сломалось. Твоя задача — написать тест для шага «найди работу → нажми «В корзину» → убедись что появилось toast-сообщение об успехе».',
    '',
    '**Что делать.**',
    '1. В правой панели открыт **editor** с заготовкой Selenium-теста (TypeScript-style mock-driver API). Импорты `By`, `driver`, `expect` уже есть.',
    '2. Напиши тело теста (5-7 строк). Что нужно сделать:',
    '   - Открыть `/listings` (`driver.navigate().to(...)`)',
    '   - Найти карточку работы по `data-testid="artwork-card-art_lera_014"` (Лерина «Vapor #14»)',
    '   - Внутри карточки найти кнопку «В корзину» (`[data-testid="add-to-cart"]`)',
    '   - Нажать на кнопку (`.click()`)',
    '   - Дождаться появления toast-сообщения (`[data-testid="cart-toast"]`)',
    '   - Проверить что текст toast-а содержит «Добавлено» (`expect(...).toContain(...)`)',
    '3. Жми **Run** в editor-панели. Mock-driver выполнит твой скрипт против реального DOM Atelier · покажет результат (Pass / Fail с stack trace).',
    '4. Когда тест зелёный — отметь rubric items (которые соответствуют тому что ты сделал) и нажми «Сдать».',
    '',
    '**Что важно.** Это твой первый Selenium-script. Структура любого UI-теста: **navigate → find → interact → wait → assert**. Запомни этот pattern — все 50+ тестов которые ты напишешь в Block 3 будут на нём построены. Между «interact» (.click()) и «assert» (.expect(...)) часто нужен `wait` шаг — потому что DOM не обновляется мгновенно. В этом тесте `driver.findElement([data-testid="cart-toast"])` сам ждёт появления элемента (mock-driver-у задана default timeout 3s).',
  ].join('\n'),
  rubric: {
    items: [
      {
        id: 'r1',
        text: 'Скрипт начинается с `driver.navigate().to("/listings")` (или эквивалентом)',
        weight: 1,
      },
      { id: 'r2', text: 'Использует `data-testid` selector-ы (не CSS-классы или XPath по тексту)', weight: 1 },
      {
        id: 'r3',
        text: 'Находит кнопку «В корзину» через `findElement` с `By.cssSelector` или `By.testId`',
        weight: 1,
      },
      { id: 'r4', text: 'Кликает по кнопке через `.click()` (не через `executeScript`)', weight: 1 },
      {
        id: 'r5',
        text: 'Ждёт появления toast-а через `findElement([data-testid="cart-toast"])` (mock-driver auto-wait) или explicit `driver.wait(until.elementLocated(...))`',
        weight: 1,
      },
      { id: 'r6', text: 'Проверяет содержимое toast-а через `expect(...).toContain("Добавлено")` или эквивалент', weight: 1 },
      { id: 'r7', text: 'Тест проходит зелёным (нет syntax errors · нет timeout-ов · assertion прошла)', weight: 2 },
    ],
    passThreshold: 6,
  },
  sampleAnswer: [
    "import { driver, By, until, expect } from '@atelier/test-runner';",
    '',
    "test('Добавление работы в корзину показывает toast', async () => {",
    "  await driver.navigate().to('/listings');",
    '',
    "  const artworkCard = await driver.findElement(By.testId('artwork-card-art_lera_014'));",
    "  const addBtn = await artworkCard.findElement(By.testId('add-to-cart'));",
    '  await addBtn.click();',
    '',
    "  const toast = await driver.findElement(By.testId('cart-toast'));",
    '  const text = await toast.getText();',
    "  expect(text).toContain('Добавлено');",
    '});',
  ].join('\n'),
  breakdown: [
    '**Senior-вариант** добавляет 3 вещи (но для junior — оверkill сейчас):',
    '',
    '1. **Page Object** · вместо `findElement` напрямую · обёртка `ListingsPage.addToCart(artworkId)`. Мы это будем учить в S14 — пока пиши «плоско».',
    '2. **Test data fixture** · `art_lera_014` сейчас hard-coded · в реальной автоматизации идёт из fixture-файла или test-database seed. Пока ОК захардкодить.',
    '3. **Custom matcher** · `expect(toast).toHaveSuccessMessage()` вместо `toContain("Добавлено")`. Это уже Block 3 advanced — сейчас читаемость важнее.',
    '',
    'Твой sample-answer выше — junior-grade. Этого достаточно чтобы тест работал в CI. Sophistication приходит позже когда suite растёт.',
  ].join('\n'),
  scoring: { baseScore: 8, firstAttemptBonus: 1, hintPenalty: 1, maxAttempts: 3 },
  achievementTriggers: [
    'author-junior',
    'sprint-13-required',
    'automation-architect',
  ],
  estimateMin: 25,
  skills: { 'Automation Basics': 5, 'Test Documentation': 1 },
  requires: ['selenium-runner'],
};

// ─── B3-SEL-03 · S13 · Investigate · wait-mismatch · explicit-too-short ───

const B3_SEL_03_BUG: WaitMismatchBug = {
  id: 'B1',
  kind: 'wait-mismatch',
  hotspot: '[data-testid="avatar"]',
  actualTimeoutMs: 500,
  observedLoadMs: { min: 150, max: 1200, p95: 950 },
  waitStrategy: 'explicit-too-short',
  recommendedFix:
    'Заменить `.timeout(500)` на `until.elementToBeVisible(...)` с 3000ms · adaptive (dom + visibility), либо использовать default findElement timeout (3s) без явного override. НЕ использовать `Thread.sleep(2000)` (anti-pattern · замедляет каждый run).',
  authorNote:
    'Тест Дениса для аватарки в /profile · explicit wait timeout 500ms · установлен исходя из локальной скорости (cache всегда warm). В CI cache cold → запрос /api/users/me/avatar 800-1200ms (p95=950) → race lost · 3/10 запусков FAIL. Hotspot = avatar element что не успевает появиться. Pedagogical hook: «explicit wait timeout = функция от observed p95 latency · НЕ от локального наблюдения · Thread.sleep — anti-pattern».',
};

const B3_SEL_03: Scenario = {
  id: 'B3-SEL-03',
  sprint: 13,
  tier: 'required',
  mode: 'investigate',
  rank: 'automation-architect-junior',
  startRoute: '/profile',
  mountStrategy: 'iframe-route',
  title: 'Flaky test · падает 3 раза из 10 · race condition',
  brief:
    'Денис: «Тест на профиль flaky · красный 3 раза из 10 в CI · локально ВСЕГДА зелёный». Серж: «Открой run history · посмотри stack trace на падении · и timing — что-то не успевает прогрузиться».',
  taskMd: [
    '**Контекст.** Тест `Profile · отображает аватарку текущего пользователя` написан 5 дней назад · работал зелёным локально 50 запусков подряд. В CI — flaky: 7/10 PASS, 3/10 FAIL. Сообщение об ошибке: `AssertionError: expected element to be visible, got hidden`. Denis жалуется потому что его PR блокируется на этом тесте — приходится 2-3 раза перезапускать pipeline.',
    '',
    '**Что делать.**',
    '1. В правой панели открыт **CI run history** (10 последних запусков теста) с timing данными для каждого:',
    '   - PASS-runs: avatar element visible через ~150-300ms после `navigate()`',
    '   - FAIL-runs: avatar element НЕ visible через 500ms (default `findElement` timeout) — fallback DOM показывает placeholder grey-circle',
    '2. Открой `/profile` в Atelier sandbox-е · открой DevTools → **Network** tab.',
    '3. Глянь как загружается аватарка: какой запрос идёт? Сколько ms он занимает? В 80% случаев быстро (cache hit), в 20% медленно (cache miss · API запрос).',
    '4. Открой test-snippet в правой панели: какой `wait` стоит на element-е аватарки? Какой timeout?',
    '5. Найди в snippet строку, в которой зашит timeout — кликни на неё. Это место где тест становится flaky.',
    '',
    '**Что важно.** Flaky test = тест который проходит **иногда**, но не всегда. Это специфический automation-bug · хуже чем «всегда красный» · потому что команда теряет доверие к suite («ну он опять flaky, перезапусти»). Причина в 80% случаев — **race condition**: тест ожидает быстрее чем DOM/API успевают прогрузиться. Решение — **explicit wait** с правильным timeout-ом или через `until.elementToBeClickable` · НЕ implicit thread.sleep(2000) (anti-pattern · замедляет всю suite). В этом сценарии: timeout 500ms был установлен исходя из локальной скорости (cache всегда warm). В CI cache cold → запрос 800-1200ms → race lost.',
  ].join('\n'),
  bugs: [B3_SEL_03_BUG],
  hints: [
    {
      text: 'Сравни PASS-runs и FAIL-runs в CI history. PASS-runs avatar появляется за 150-300ms · FAIL-runs за >500ms. Какой-то параметр в test-е равен 500ms — найди его.',
    },
    {
      text: 'В test-snippet есть строка `driver.findElement(By.testId("avatar")).timeout(500)`. Эта `.timeout(500)` — explicit wait в 500ms. Когда CI cache холодный · запрос дольше · тест не дожидается. Кликни на эту строку.',
    },
    {
      text: 'Решение: увеличить timeout до 3000ms или использовать `until.elementToBeVisible(...)` · которое адаптивно ждёт. Hard-codeить 2000ms через `Thread.sleep` — anti-pattern (замедлит каждый run на 2s). Кликни сейчас на строку `.timeout(500)` — это и есть локация баг-а.',
    },
  ],
  scoring: { baseScore: 7, firstAttemptBonus: 1, hintPenalty: 1, maxAttempts: 3 },
  achievementTriggers: [
    'detective-junior',
    'sprint-13-required',
    'automation-architect',
  ],
  estimateMin: 20,
  skills: { 'Automation Basics': 5, 'Practice Lab': 5, 'Non-functional Testing': 1 },
  requires: ['selenium-runner'],
};

// ─── B3-SEL-04 · S13 · Investigate · selector-mismatch (class-removed) ──────

const B3_SEL_04_BUG: SelectorMismatchBug = {
  id: 'B1',
  kind: 'selector-mismatch',
  hotspot: '[data-testid="add-to-cart"]',
  expectedSelector: '.cart-button',
  actualSelector: '[data-testid="add-to-cart"]',
  mismatchReason: 'class-removed',
  authorNote:
    'Старый Selenium-тест Дениса искал кнопку «В корзину» через CSS-класс `.cart-button`. Frontend-команда сделала refactor: убрала legacy classes · заменила на BEM-naming · класс `.cart-button` исчез. Stable selector остался — `data-testid="add-to-cart"` (он всегда был). Hotspot = кнопка с правильным data-testid. Pedagogical hook: «CSS classes — visual styling, не contract for tests · могут уйти при любом refactor · `data-testid` — stable contract».',
};

const B3_SEL_04: Scenario = {
  id: 'B3-SEL-04',
  sprint: 13,
  tier: 'required',
  mode: 'investigate',
  rank: 'automation-architect-junior',
  startRoute: '/listings',
  mountStrategy: 'iframe-route',
  title: 'Старый тест ищет CSS-класс · после refactor класс исчез',
  brief:
    'Денис: «Мой тест на cart-add теперь падает: NoSuchElementException на .cart-button. А вчера всё работало». Илья из frontend-команды: «А, мы вчера выкатили BEM refactor · убрали legacy classes». Серж: «Какой stable selector вместо .cart-button?»',
  taskMd: [
    '**Контекст.** Денис написал тест на cart-flow месяц назад · использовал CSS-class `.cart-button` для locator. Frontend-команда (Илья · Маша) вчера выкатила BEM-naming refactor: `.cart-button` → `.product-card__cta--add` (уродливо но семантично BEM-style). Тест Дениса падает в CI. Денис в Slack: «WTF, я не трогал тест!»',
    '',
    '**Что делать.**',
    '1. Открой `/listings` в Atelier sandbox-е · найди карточку работы · кнопку «В корзину».',
    '2. Открой DevTools → Elements · кликни на кнопку. Посмотри её attributes:',
    '   - Какие у неё `class` (видны новые BEM-имена)',
    '   - Какие `data-*` (видны stable test-attributes)',
    '3. В правой панели открыт **migration log** · показывает diff: старый CSS-класс vs новый. Глянь чтобы понять что именно сменилось.',
    '4. В test-snippet Дениса видна старая строка: `By.cssSelector(".cart-button")`. Какой **stable** selector нужно использовать вместо?',
    '5. Кликни на кнопку «В корзину» · это будет правильный target.',
    '',
    '**Что важно.** Это второй вариант selector-mismatch (после B3-SEL-01). Там разработчик переименовал `data-testid`. Здесь — другой кейс: CSS-класс ушёл при refactor. Урок шире: **CSS classes принадлежат visual styling layer · не test contract layer**. Каждый CSS refactor может их сломать. `data-testid` (или `data-cy`, `data-test`) — это **отдельный namespace для тестов** · никакая стилевая правка их не трогает. Когда увидишь в чужом тесте `By.cssSelector(".some-class")` — это red flag · scenario для refactor. В реальной команде есть convention: все automation-locators через `data-testid` · CSS-classes только для styling.',
  ].join('\n'),
  bugs: [B3_SEL_04_BUG],
  hints: [
    {
      text: 'У кнопки «В корзину» сейчас два «слоя» атрибутов: класс (для CSS) и `data-testid` (для тестов). Какой из них «выживет» при следующем CSS refactor?',
    },
    {
      text: 'В DOM кнопка имеет: `class="product-card__cta--add"` (BEM) и `data-testid="add-to-cart"` (test contract). Класс изменился сегодня · `data-testid` существует с самого начала. Что выбрать как stable selector?',
    },
    {
      text: '`data-testid="add-to-cart"` — правильный locator для теста. Кликни на кнопку «В корзину» в Atelier-странице сейчас — это правильный target (Selenium бы тоже его ткнул через `By.cssSelector("[data-testid=\'add-to-cart\']")`).',
    },
  ],
  scoring: { baseScore: 6, firstAttemptBonus: 1, hintPenalty: 1, maxAttempts: 3 },
  achievementTriggers: [
    'detective-junior',
    'sprint-13-required',
    'automation-architect',
  ],
  estimateMin: 18,
  skills: { 'Automation Basics': 5, 'Practice Lab': 4 },
  requires: ['selenium-runner'],
};

// ─── B3-SEL-05 · S13 · Investigate · wait-mismatch (thread-sleep anti-pattern) ─

const B3_SEL_05_BUG: WaitMismatchBug = {
  id: 'B1',
  kind: 'wait-mismatch',
  hotspot: '[data-testid="cart-toast"]',
  actualTimeoutMs: 0,
  observedLoadMs: { min: 200, max: 600, p95: 480 },
  waitStrategy: 'thread-sleep',
  recommendedFix:
    'Удалить `await sleep(2000)` · использовать `until.elementToBeVisible([data-testid="cart-toast"])` с 3000ms timeout · adaptive wait разрешает race без замедления каждого run на 2 секунды.',
  authorNote:
    'Анти-паттерн: тест содержит `await new Promise(resolve => setTimeout(resolve, 2000))` (Thread.sleep equivalent) · «работает» в локальной среде · но **скрывает** реальную проблему: cart-toast иногда задерживается в CI · но 2s sleep всегда дольше · race не виден. Фокус: каждый run теста замедляется на 2 секунды (с 50 тестами в suite = +100s = +1.6 минуты). Hotspot = cart-toast element. Pedagogical hook: «Thread.sleep — anti-pattern · он маскирует timing problems вместо того чтобы их fix-ить · explicit wait честно ждёт пока element ready».',
};

const B3_SEL_05: Scenario = {
  id: 'B3-SEL-05',
  sprint: 13,
  tier: 'required',
  mode: 'investigate',
  rank: 'automation-architect-junior',
  startRoute: '/listings',
  mountStrategy: 'iframe-route',
  title: 'Тест зелёный но suite медленный · Thread.sleep инфекция',
  brief:
    'Маша: «Suite уже 12 минут гоняется · добавили только 5 тестов за неделю но время выросло на 4 минуты». Серж: «Где-то Thread.sleep инфекция · открой test-snippets · ищи setTimeout без условия».',
  taskMd: [
    '**Контекст.** В команде уже 30 Selenium-тестов · suite раньше гонялась 8 минут. Маша добавила 5 новых тестов за неделю · ожидала ~10 минут. Сейчас 12 минут — каждый из 5 новых тестов добавил +30 секунд. Серж подозревает Thread.sleep anti-pattern. Тесты зелёные · но suite ползёт.',
    '',
    '**Что делать.**',
    '1. В правой панели открыт **suite timing report** — показывает сколько каждый из 30 тестов длится. Большинство 8-15 секунд · 5 новых — 38-45 секунд. Подозрительно ровно.',
    '2. Открой test-snippet `cart-toast-visibility.test.ts` (один из новых · показан в editor). Прочитай код.',
    '3. Найди строку которая делает test медленным **искусственно** · не из-за реальной работы. Hint: ищи async sleep без условия (`setTimeout` · `sleep` · `wait` без условия).',
    '4. Кликни на toast-element в Atelier-странице (когда добавишь работу в корзину) — это hotspot bug-а (toast появляется быстрее чем sleep ждёт).',
    '',
    '**Что важно.** `setTimeout(resolve, 2000)` (или `Thread.sleep(2000)` в Java) — anti-pattern. Тест говорит: «жди ровно 2 секунды независимо от того, готов ли element». Если element появляется через 200ms — теряем 1.8s впустую. Если element появится через 2.5s — race lost · падение. Решение — **explicit wait с условием**: `until.elementToBeVisible(toastSelector)` ждёт **только пока нужно** · максимум до timeout-а. Когда наследуешь чужой test и видишь `sleep(2000)` — это red flag. Заменяй на explicit wait + удалишь 1-2 секунды timing-а с каждого run-а.',
  ].join('\n'),
  bugs: [B3_SEL_05_BUG],
  hints: [
    {
      text: 'В test-snippet ищи строку с `setTimeout` или `sleep` или `await new Promise(resolve => setTimeout(...))`. Это «магическое» ожидание без условия — anti-pattern.',
    },
    {
      text: 'Найдена строка `await new Promise(r => setTimeout(r, 2000))` после клика на «В корзину». Эта 2-секундная пауза — то что замедляет каждый run. Toast реально появляется за 200-600ms (p95 = 480ms). Кликни на toast-element когда увидишь его в Atelier.',
    },
    {
      text: 'Решение: заменить `setTimeout` на `await driver.wait(until.elementLocated(toastSelector), 3000)`. Тест будет занимать столько, сколько реально нужно (300-600ms) · не фиксированные 2s. Toast в Atelier — это hotspot (он появляется быстрее чем sleep ждёт). Кликни на него.',
    },
  ],
  scoring: { baseScore: 6, firstAttemptBonus: 1, hintPenalty: 1, maxAttempts: 3 },
  achievementTriggers: [
    'detective-junior',
    'sprint-13-required',
    'automation-architect',
  ],
  estimateMin: 18,
  skills: { 'Automation Basics': 5, 'Non-functional Testing': 1 },
  requires: ['selenium-runner'],
};

// ─── B3-SEL-06 · S13 · Author · data-driven test (parameterized loop · CSV) ──

const B3_SEL_06: Scenario = {
  id: 'B3-SEL-06',
  sprint: 13,
  tier: 'required',
  mode: 'author',
  rank: 'automation-architect-junior',
  startRoute: '/listings',
  mountStrategy: 'iframe-route',
  title: 'Data-driven тест · 5 разных работ через CSV',
  brief:
    'Серж: «Хочу один тест который проверяет добавление в корзину для 5 разных работ. Не пиши 5 копий — пиши один параметризованный · читай данные из CSV». Маша: «А что в CSV?» Серж кидает таблицу · 5 строк · колонки artworkId/expectedTitle/expectedPriceRub/inStock.',
  taskMd: [
    '**Контекст.** В B3-SEL-02 ты написал тест для одной работы (`art_lera_014`). В реальной команде покрытие = 50+ работ · и писать 50 копий теста (один-к-одному) — не масштабируется. Решение — **data-driven testing** (DDT): один тест-сценарий · много input-комбинаций · loop по данным.',
    '',
    'CSV-файл уже создан в правой панели:',
    '',
    '```',
    'artworkId,expectedTitle,expectedPriceRub,inStock',
    'art_lera_014,Vapor #14,3500,true',
    'art_lera_007,Vapor #07,3200,false',
    'art_anya_22,Soft Loop,4800,true',
    'art_serj_03,Grain Studies #3,2100,true',
    'art_masha_11,Late Bloom,5400,true',
    '```',
    '',
    '**Что делать.**',
    '1. В editor-панели уже есть skeleton с импортами (`driver`, `By`, `until`, `expect`, `parseCSV`). `parseCSV("data/artworks-cart.csv")` возвращает array of rows.',
    '2. Напиши один тест который:',
    '   - Читает CSV файл в начале',
    '   - Делает loop по rows (`for-of` или `forEach`)',
    '   - Для каждой row: открывает `/listings` · находит карточку через `data-testid="artwork-card-${row.artworkId}"` · ',
    '   - **Если** `row.inStock === "true"` — нажимает «В корзину» · ждёт toast · проверяет «Добавлено»',
    '   - **Иначе** — проверяет что у карточки есть бейдж 🔒 «Распродано» (`data-testid="sold-out-badge"`)',
    '   - Логирует результат для каждой row (`console.log` ОК для junior)',
    '3. Жми **Run** · mock-driver исполнит тест 5 раз (по одному на row). В отчёте увидишь 4 PASS (in-stock works) + 1 PASS (sold-out branch · Vapor #07) = 5 итераций.',
    '4. Когда все iteration-ы зелёные — отметь rubric items + сдай.',
    '',
    '**Что важно.** Data-driven testing меняет mindset: вместо «один тест = один сценарий» · «один тест = одна логика, прогоняется по N данным». Преимущества:',
    '',
    '- **DRY** (Don\'t Repeat Yourself) · 1 кода вместо 5 копий',
    '- **Расширяемость** · добавить 6-ую работу = добавить строку в CSV · код теста не трогается',
    '- **Coverage matrix** · легко увидеть какие cases покрыты (CSV = matrix)',
    '- **Нюанс** · если 1 row падает · нужно показать **какая именно** (логирование row.artworkId важно для debug)',
    '',
    'В реальном production-suite CSV заменяется на JSON · TypedFixtures · базу данных. Pattern остаётся тот же — один тест, много данных.',
  ].join('\n'),
  rubric: {
    items: [
      { id: 'r1', text: 'Читает CSV через `parseCSV(...)` (или эквивалент) · сохраняет в array', weight: 1 },
      { id: 'r2', text: 'Использует `for-of` или `forEach` loop по rows (не 5 копий теста)', weight: 1 },
      {
        id: 'r3',
        text: 'Для каждой row строит selector через template literal `data-testid="artwork-card-${row.artworkId}"`',
        weight: 1,
      },
      {
        id: 'r4',
        text: 'Branch на `row.inStock` · разная логика для in-stock (add to cart) vs sold-out (badge check)',
        weight: 1,
      },
      { id: 'r5', text: 'In-stock branch: click + wait + assert toast «Добавлено»', weight: 1 },
      { id: 'r6', text: 'Sold-out branch: assert наличие `data-testid="sold-out-badge"` элемента', weight: 1 },
      { id: 'r7', text: 'Логирует `row.artworkId` для каждой iteration (debug-friendly при падении)', weight: 1 },
      { id: 'r8', text: 'Все 5 iteration-ов проходят · suite зелёная', weight: 2 },
    ],
    passThreshold: 7,
  },
  sampleAnswer: [
    "import { driver, By, until, expect, parseCSV } from '@atelier/test-runner';",
    '',
    "test('Cart-flow для всех артвортов из CSV', async () => {",
    "  const rows = await parseCSV('data/artworks-cart.csv');",
    '',
    '  for (const row of rows) {',
    "    console.log(`Testing artwork: ${row.artworkId}`);",
    "    await driver.navigate().to('/listings');",
    '',
    '    const card = await driver.findElement(',
    "      By.testId(`artwork-card-${row.artworkId}`),",
    '    );',
    '',
    "    if (row.inStock === 'true') {",
    "      const addBtn = await card.findElement(By.testId('add-to-cart'));",
    '      await addBtn.click();',
    '      const toast = await driver.wait(',
    "        until.elementLocated(By.testId('cart-toast')),",
    '        3000,',
    '      );',
    "      expect(await toast.getText()).toContain('Добавлено');",
    '    } else {',
    "      const badge = await card.findElement(By.testId('sold-out-badge'));",
    '      expect(await badge.isDisplayed()).toBe(true);',
    '    }',
    '  }',
    '});',
  ].join('\n'),
  breakdown: [
    '**Senior-вариант** добавляет 3 темы (для junior сейчас оверkill):',
    '',
    '1. **Test isolation** · каждая iteration сейчас зависит от предыдущей (cart накапливается). Senior бы добавил `beforeEach: clearCart()` чтобы изолировать. Block 3 advanced.',
    '2. **Parallelization** · 5 iterations sequential = 5x slower. Modern frameworks (Playwright `test.describe.parallel`) умеют параллелить data-driven cases. S15 затронем.',
    '3. **Soft assertions** · если row 3 fail-ит · loop останавливается · cases 4-5 не проверились. Senior использует try/catch + сбор ошибок · отчёт «3/5 passed». Это framework-specific (Mocha · Jest · Vitest).',
    '',
    'Твой sample-answer — junior-grade · линейный loop · honest assertions · clear logging. Этого достаточно чтобы построить первое DDT-покрытие. Sophistication приходит когда test count > 50.',
  ].join('\n'),
  scoring: { baseScore: 9, firstAttemptBonus: 1, hintPenalty: 1, maxAttempts: 3 },
  achievementTriggers: [
    'author-junior',
    'sprint-13-required',
    'automation-architect',
    'test-design',
  ],
  estimateMin: 30,
  skills: { 'Automation Basics': 5, 'Test Design': 3, 'Test Documentation': 1 },
  requires: ['selenium-runner'],
};

// ─── B3-SEL-07 · S13 · Investigate · selector-mismatch (XPath fragility) ────

const B3_SEL_07_BUG: SelectorMismatchBug = {
  id: 'B1',
  kind: 'selector-mismatch',
  hotspot: '[data-testid="logout-link"]',
  expectedSelector: '/html/body/div[1]/header/nav/ul/li[3]/a',
  actualSelector: '[data-testid="logout-link"]',
  mismatchReason: 'structure-shifted',
  authorNote:
    'Тест Маши использует absolute XPath `/html/body/.../li[3]/a` чтобы найти ссылку «Выйти» в навбаре. Frontend добавил новый пункт меню («Уведомления») между «Поддержка» и «Выйти» · теперь «Выйти» это `li[4]/a` · не `li[3]/a`. XPath сломался · хотя ссылка на месте · `data-testid="logout-link"` не изменился. Hotspot = ссылка «Выйти». Pedagogical hook: «absolute XPath = anti-pattern · любая структурная перестановка ломает · prefer data-testid · если XPath нужен — использовать relative с anchor (//*[@data-testid="..."]/a)».',
};

const B3_SEL_07: Scenario = {
  id: 'B3-SEL-07',
  sprint: 13,
  tier: 'bonus',
  mode: 'investigate',
  rank: 'automation-architect-junior',
  startRoute: '/dashboard',
  mountStrategy: 'iframe-route',
  title: 'XPath ломается после добавления пункта меню',
  brief:
    'Маша: «Тест на logout сегодня красный · но я ничего не меняла». Илья: «Мы вчера добавили пункт меню Уведомления · может XPath?» Серж: «Открой test-snippet · если там absolute XPath — это и есть антипаттерн».',
  taskMd: [
    '**Контекст.** В навбаре было 4 пункта · «Лента / Поддержка / Профиль / Выйти». Тест Маши использует absolute XPath `/html/body/div[1]/header/nav/ul/li[4]/a` чтобы найти «Выйти». Вчера frontend добавил между «Поддержка» и «Профиль» новый пункт «Уведомления» (5 пунктов теперь). XPath индекс `li[4]` теперь указывает на «Профиль» · не «Выйти». Тест кликает не туда · падает.',
    '',
    '**Что делать.**',
    '1. Открой `/dashboard` в Atelier · посмотри навбар. Сосчитай пункты слева направо.',
    '2. Открой DevTools → Elements · разверни `<nav>` element. Глянь сколько `<li>` сейчас (5) и какой `data-testid` у каждой ссылки.',
    '3. В правой панели test-snippet: `By.xpath("/html/body/div[1]/header/nav/ul/li[4]/a")`. Это absolute XPath — отсчитывает позицию-в-структуре.',
    '4. Какой stable selector у ссылки «Выйти»?',
    '5. Кликни на ссылку «Выйти» в навбаре — это и есть target.',
    '',
    '**Что важно.** XPath бывает двух типов:',
    '',
    '- **Absolute** · `/html/body/.../li[3]/a` · отсчитывает позицию от корня. Любая структурная перестановка (добавили `<div>` · переместили `<nav>` · вставили `<li>`) — сломает.',
    '- **Relative с anchor** · `//*[@data-testid="logout-link"]` · ищет element по test-attribute · structure-independent.',
    '',
    'Absolute XPath — anti-pattern в automation. Это hard-coded путь от корня документа · ломается при любой DOM-перестановке. Если XPath нужен (нет `data-testid` · нет id · нет stable class) — используй relative form с anchor: `//div[@class="cart"]//button[contains(text(), "Купить")]`. Это всё равно fragile · но менее.',
    '',
    'Best practice (industry consensus 2024+): **prefer `data-testid` над любым XPath**. XPath legacy · оставлен для редких cases где нет другого способа.',
  ].join('\n'),
  bugs: [B3_SEL_07_BUG],
  hints: [
    {
      text: 'В navbar сейчас 5 пунктов: Лента / Поддержка / Уведомления (NEW) / Профиль / Выйти. Старый XPath `li[4]` смотрит на Профиль · не на Выйти. Какой data-attribute есть у ссылки «Выйти»?',
    },
    {
      text: 'У ссылки «Выйти» есть `data-testid="logout-link"`. Это stable selector — не зависит от позиции в структуре навбара. Кликни на ссылку «Выйти».',
    },
    {
      text: 'Решение: заменить `By.xpath(...)` на `By.cssSelector("[data-testid=\'logout-link\']")`. Если по какой-то причине нужен XPath — использовать relative: `By.xpath("//*[@data-testid=\'logout-link\']")`. Кликни на «Выйти» сейчас — это правильный target.',
    },
  ],
  scoring: { baseScore: 7, firstAttemptBonus: 1, hintPenalty: 1, maxAttempts: 3 },
  achievementTriggers: [
    'detective-junior',
    'sprint-13-bonus',
    'automation-architect',
    'curious-cat',
  ],
  estimateMin: 22,
  skills: { 'Automation Basics': 5, 'Practice Lab': 4 },
  requires: ['selenium-runner'],
};

// ─── B3-SEL-08 · S13 · Bonus · Author · refactor flaky test для stability ───

const B3_SEL_08: Scenario = {
  id: 'B3-SEL-08',
  sprint: 13,
  tier: 'bonus',
  mode: 'author',
  rank: 'automation-architect-junior',
  startRoute: '/checkout',
  mountStrategy: 'iframe-route',
  title: 'Refactor flaky test · стабилизируй checkout-flow тест',
  brief:
    'Серж: «У нас есть тест который падает 4 раза из 10. Я тебе дам его как есть · ты его перепиши так чтобы был стабильным. Список проблем: 1) использует absolute XPath · 2) Thread.sleep(3000) в середине · 3) проверяет finished URL без wait. Исправь все три».',
  taskMd: [
    '**Контекст.** Тест ниже работал когда был написан 3 месяца назад · сейчас flaky. Денис уже расследовал · у него 3 root causes. Твоя задача — рефакторить · применяя всё что знаешь из B3-SEL-01 .. B3-SEL-07.',
    '',
    'Текущий test-snippet (broken):',
    '',
    '```typescript',
    "test('Checkout completes successfully', async () => {",
    "  await driver.navigate().to('/cart');",
    "  // 1. absolute XPath — fragile, ломается при добавлении нового button-а",
    "  const checkoutBtn = await driver.findElement(",
    "    By.xpath('/html/body/div[1]/main/div/section[2]/button[1]')",
    '  );',
    '  await checkoutBtn.click();',
    "  // 2. Thread.sleep — anti-pattern, замедляет suite",
    '  await new Promise(r => setTimeout(r, 3000));',
    "  // 3. URL check без wait — race lost если редирект медленный",
    '  const url = await driver.getCurrentUrl();',
    "  expect(url).toContain('/checkout/complete');",
    '});',
    '```',
    '',
    '**Что делать.**',
    '',
    'Перепиши тест в правом editor-е · убрав все 3 проблемы:',
    '',
    '1. **Замени absolute XPath на data-testid.** У кнопки оплаты есть `data-testid="checkout-btn"` · используй `By.cssSelector("[data-testid=\'checkout-btn\']")` или `By.testId("checkout-btn")`.',
    '2. **Замени Thread.sleep на explicit wait.** Вместо `setTimeout 3000ms` используй `driver.wait(until.urlContains("/checkout/complete"), 5000)` — adaptive wait для URL.',
    '3. **Убедись что URL check — после wait.** Wait блокирует пока URL содержит «/checkout/complete» · если timeout истёк — wait сам бросит TimeoutError. После wait дополнительный `expect(url).toContain(...)` уже redundant · можно оставить для readable assertion · но **только** после wait.',
    '',
    '**Бонус-pattern** (если есть время · заодно):',
    '',
    '4. Добавь `try/catch` вокруг wait · в catch напиши понятный error message: `throw new Error("Checkout не редиректнул на /complete за 5s · возможно payment API сломан")`. Это поможет debug-у когда тест падает в CI.',
    '',
    '**Что важно.** Это твой первый refactor exercise. В реальной работе 30-40% времени automation engineer-а — починка/стабилизация существующих тестов · не написание новых. Знание трёх pattern-ов выше (data-testid · explicit wait · adaptive URL wait) покрывает 80% flaky cases. Все три ты увидел в Investigate-сценариях S13 (B3-SEL-01/03/04/05/07) · теперь применяешь их одновременно. Это уровень «junior+» · готовность к real-world maintenance.',
  ].join('\n'),
  rubric: {
    items: [
      { id: 'r1', text: 'Заменил `By.xpath(...)` на `By.cssSelector("[data-testid=\'checkout-btn\']")` или эквивалент', weight: 1 },
      { id: 'r2', text: 'Удалил `setTimeout`/`sleep` · заменил на `driver.wait(until.urlContains(...), N)`', weight: 2 },
      { id: 'r3', text: 'Wait timeout установлен в разумное окно (3000-10000ms · не 500ms · не 30000ms)', weight: 1 },
      { id: 'r4', text: 'URL assertion стоит **после** wait (не до · не вместо)', weight: 1 },
      { id: 'r5', text: 'Тест проходит зелёным · стабильно · 10 раз подряд', weight: 2 },
      {
        id: 'r6',
        text: '**Бонус-pattern** · добавил try/catch с понятным error message для debug',
        weight: 1,
      },
    ],
    passThreshold: 6,
  },
  sampleAnswer: [
    "test('Checkout completes successfully', async () => {",
    "  await driver.navigate().to('/cart');",
    '',
    '  const checkoutBtn = await driver.findElement(',
    "    By.cssSelector('[data-testid=\"checkout-btn\"]'),",
    '  );',
    '  await checkoutBtn.click();',
    '',
    '  try {',
    "    await driver.wait(until.urlContains('/checkout/complete'), 5000);",
    '  } catch (err) {',
    '    throw new Error(',
    "      'Checkout не редиректнул на /complete за 5s · возможно payment API сломан · ' +",
    "      `original: ${err.message}`,",
    '    );',
    '  }',
    '',
    '  const url = await driver.getCurrentUrl();',
    "  expect(url).toContain('/checkout/complete');",
    '});',
  ].join('\n'),
  breakdown: [
    '**Senior-вариант** ушёл бы дальше · но для bonus-tier это уже отличный refactor:',
    '',
    '1. **Page Object для checkout** · `CheckoutPage.complete()` инкапсулирует click + wait + assertion · тест становится 1 строка `await CheckoutPage.complete()`. Учим в S14.',
    '2. **Custom wait condition** · `until.checkoutComplete` (custom predicate) выразительнее чем `urlContains` · self-documenting.',
    '3. **Retry policy** · если payment API flaky · senior мог бы добавить `flaky-test-retry` (1 retry) на уровне runner config · НЕ внутри теста. Это framework-level concern.',
    '',
    'Твой refactor — junior+ level: устранил 3 anti-pattern-а одновременно. В команде это уже maintainable test.',
  ].join('\n'),
  scoring: { baseScore: 8, firstAttemptBonus: 1, hintPenalty: 1, maxAttempts: 3 },
  achievementTriggers: [
    'author-junior',
    'sprint-13-bonus',
    'automation-architect',
    'curious-cat',
  ],
  estimateMin: 30,
  skills: { 'Automation Basics': 5, 'Test Design': 2 },
  requires: ['selenium-runner'],
};

// ────────────────────────────────────────────────────────────────────────────
// Sprint 14 · Selenium + POM patterns — B3-POM-01..06
// ────────────────────────────────────────────────────────────────────────────

// ─── B3-POM-01 · S14 · Investigate · stale element reference (POM gotcha) ───

const B3_POM_01: Scenario = {
  id: 'B3-POM-01',
  sprint: 14,
  tier: 'required',
  mode: 'investigate',
  rank: 'automation-architect-junior',
  startRoute: '/login',
  mountStrategy: 'iframe-route',
  customModule: 'block-3/pom-stale-reference.tsx',
  title: 'POM падает · StaleElementReferenceException на usernameInput',
  brief:
    'Денис написал первый Page Object для login. Тест проходит локально 1 раз · второй раз падает: StaleElementReferenceException. Серж: «Глянь как Денис кэширует элементы в POM-е. Element-references — не вечные».',
  taskMd: [
    '**Контекст.** Денис написал первый Page Object — `LoginPage`. Внутри класса он сделал поле `private usernameInput = driver.findElement(By.testId("login-username"))` · инициализирует в constructor-е. Каждый метод `LoginPage.fillUsername(value)` использует этот закэшированный reference.',
    '',
    'Запуск 1 (локально, fresh): тест зелёный.',
    'Запуск 2 (тот же тест, immediately): `StaleElementReferenceException: element is not attached to the page document`.',
    '',
    '**Что делать.**',
    '1. В правой панели открыт `LoginPage.ts` · посмотри код · обрати внимание на как объявлены поля `usernameInput` / `passwordInput` / `submitBtn`.',
    '2. Открой DevTools на `/login` · обрати внимание: после первого navigate React re-render-ит `<form>` · DOM nodes пересоздаются при каждом mount-е страницы.',
    '3. В test-snippet видна последовательность: `const loginPage = new LoginPage(); await loginPage.fillUsername("anya@art"); await driver.navigate().to("/login"); await loginPage.fillPassword("***");`. На первом fillUsername всё ок · на втором (после navigate) — element reference указывает на старый DOM node, которого уже нет.',
    '4. Кликни на поле `passwordInput` в форме (то самое которое в POM-е закэшировано неверно) — это hotspot.',
    '',
    '**Что важно.** Это POM gotcha #1. **Element reference в Selenium — это указатель на конкретный DOM node**, не на selector. Когда страница re-render-ит form, старый DOM node detach-ится · reference становится stale (буквально «протухший»). Вариант 1 решения — **lazy getter**: вместо `private usernameInput = ...` использовать `private get usernameInput() { return driver.findElement(By.testId("login-username")); }` — каждый раз свежий fetch. Вариант 2 — **method-scoped fetch**: каждый method сам делает findElement. Вариант 1 чище.',
  ].join('\n'),
  hints: [
    {
      text: 'Что значит StaleElementReferenceException? Это значит что reference на DOM node устарел — node удалили из document (re-render). Когда мы могли его удалить? Между двумя navigate-ами.',
    },
    {
      text: 'В LoginPage поля `usernameInput` / `passwordInput` / `submitBtn` инициализируются ОДИН раз в constructor-е. После navigate() React re-render-ит форму · старые references устаревают. Кликни на поле password в форме (то что должно быть переинициализировано).',
    },
    {
      text: 'Решение: заменить `private usernameInput = driver.findElement(...)` на `private get usernameInput() { return driver.findElement(...) }` (lazy getter — каждый вызов свежий fetch). Кликни на password input — это и есть hotspot.',
    },
  ],
  scoring: { baseScore: 7, firstAttemptBonus: 1, hintPenalty: 1, maxAttempts: 3 },
  achievementTriggers: [
    'detective-junior',
    'sprint-14-required',
    'automation-architect',
  ],
  estimateMin: 22,
  skills: { 'Automation Patterns': 5, 'Practice Lab': 4 },
  requires: ['selenium-runner', 'pom-library'],
};

// ─── B3-POM-02 · S14 · Author · напиши первый Page Object · LoginPage ───────

const B3_POM_02: Scenario = {
  id: 'B3-POM-02',
  sprint: 14,
  tier: 'required',
  mode: 'author',
  rank: 'automation-architect-junior',
  startRoute: '/login',
  mountStrategy: 'iframe-route',
  title: 'Напиши первый Page Object · LoginPage',
  brief:
    'Серж: «Время абстракции. Все наши тесты дублируют login-flow первой строкой. Заверни это в LoginPage class · с методом login(username, password). Используй lazy getters чтобы избежать stale-reference».',
  taskMd: [
    '**Контекст.** В suite уже 8 тестов · каждый начинается с одинаковых 4 строк: `navigate /login → fillUsername → fillPassword → submit`. Дублирование. Время делать первый Page Object.',
    '',
    'POM (Page Object Model) — паттерн где каждая страница приложения имеет свой class · поля = locators · методы = действия пользователя. Тесты используют POM-методы как «черный ящик» · не знают о DOM.',
    '',
    '**Что делать.**',
    '1. В editor создай class `LoginPage` в файле `pages/LoginPage.ts`.',
    '2. Используй **lazy getters** для locator-ов (избежать stale-reference per B3-POM-01):',
    '   - `usernameInput` → `[data-testid="login-username"]`',
    '   - `passwordInput` → `[data-testid="login-password"]`',
    '   - `submitBtn` → `[data-testid="login-submit"]`',
    '   - `errorBanner` → `[data-testid="login-error"]` (для assertions when login fails)',
    '3. Реализуй методы:',
    '   - `async navigate()` — `driver.navigate().to("/login")`',
    '   - `async fillUsername(value: string)` — `.sendKeys(value)`',
    '   - `async fillPassword(value: string)` — `.sendKeys(value)`',
    '   - `async submit()` — `.click()`',
    '   - `async login(username, password)` — combo · navigate → fill both → submit. Это «high-level» method которым будут пользоваться тесты.',
    '   - `async getErrorMessage()` — `errorBanner.getText()` · для assertion path',
    '4. В правой панели один **demo-test** уже написан · использует твой LoginPage. Жми Run после того как POM готов · должен пройти зелёным.',
    '5. Когда тест проходит — отметь rubric и сдавай.',
    '',
    '**Что важно.** Это твой первый POM · и pattern будет жить дальше через все automation tests. Несколько rules-of-thumb:',
    '',
    '- **Page Object не делает assertions** — он только инкапсулирует locators + actions. Assertions — в test-е (test знает «что должно быть»).',
    '- **Метод названия по действиям пользователя** — `login(username, password)` · НЕ `clickLoginButton()`. Думай «что бы сказал human tester», не «какой DOM-element ткнуть».',
    '- **Lazy getters** — `private get usernameInput() { return driver.findElement(...); }` · не `private usernameInput = driver.findElement(...);`. Mandatory чтобы избежать stale-reference.',
    '- **Возврат значений · не assertions** — `getErrorMessage()` возвращает string · test уже делает `expect(msg).toContain("Invalid")`. POM не должен `expect(...)` ничего.',
  ].join('\n'),
  rubric: {
    items: [
      { id: 'r1', text: 'Class `LoginPage` экспортируется из `pages/LoginPage.ts`', weight: 1 },
      { id: 'r2', text: 'Все 4 locators объявлены через **lazy getters** (`get xxx() { return findElement(...) }`)', weight: 2 },
      { id: 'r3', text: 'Locators используют `data-testid` (не CSS-classes · не XPath)', weight: 1 },
      { id: 'r4', text: 'Метод `login(username, password)` инкапсулирует navigate + fill + submit', weight: 1 },
      { id: 'r5', text: 'Метод `getErrorMessage()` возвращает текст · НЕ делает expect внутри (POM не делает assertions)', weight: 1 },
      { id: 'r6', text: 'Методы названы по user-actions (login · submit · fillUsername) · не по DOM (clickButton)', weight: 1 },
      { id: 'r7', text: 'Demo-тест с твоим POM проходит зелёным', weight: 2 },
    ],
    passThreshold: 7,
  },
  sampleAnswer: [
    "import { driver, By } from '@atelier/test-runner';",
    '',
    'export class LoginPage {',
    "  private get usernameInput() { return driver.findElement(By.testId('login-username')); }",
    "  private get passwordInput() { return driver.findElement(By.testId('login-password')); }",
    "  private get submitBtn() { return driver.findElement(By.testId('login-submit')); }",
    "  private get errorBanner() { return driver.findElement(By.testId('login-error')); }",
    '',
    '  async navigate() {',
    "    await driver.navigate().to('/login');",
    '  }',
    '',
    '  async fillUsername(value: string) {',
    '    await this.usernameInput.sendKeys(value);',
    '  }',
    '',
    '  async fillPassword(value: string) {',
    '    await this.passwordInput.sendKeys(value);',
    '  }',
    '',
    '  async submit() {',
    '    await this.submitBtn.click();',
    '  }',
    '',
    '  async login(username: string, password: string) {',
    '    await this.navigate();',
    '    await this.fillUsername(username);',
    '    await this.fillPassword(password);',
    '    await this.submit();',
    '  }',
    '',
    '  async getErrorMessage(): Promise<string> {',
    '    return await this.errorBanner.getText();',
    '  }',
    '}',
  ].join('\n'),
  breakdown: [
    '**Senior-вариант** добавляет 3 темы (опционально для junior):',
    '',
    '1. **Constants для locators** · вынести `LOGIN_USERNAME_TESTID = "login-username"` в `pages/locators.ts` · POM импортирует константы. Помогает когда `data-testid` меняется в одном месте.',
    '2. **Async constructor pattern** · если POM нужно делать setup (initial cookies · session-init) · constructor не может быть async. Senior использует static factory `LoginPage.create()` который возвращает Promise<LoginPage>. Block 3 advanced.',
    '3. **Generic BasePage class** · если 5+ страниц имеют общие методы (waitForLoad · getCurrentUrl · screenshot) · senior выносит в abstract `BasePage` · все POMs extends это. Учим в S14 advanced lessons.',
    '',
    'Твой sample выше — junior-grade · readable · maintainable · pattern foundation. Этого достаточно чтобы 90% real-world POMs работали.',
  ].join('\n'),
  scoring: { baseScore: 9, firstAttemptBonus: 1, hintPenalty: 1, maxAttempts: 3 },
  achievementTriggers: [
    'author-junior',
    'sprint-14-required',
    'automation-architect',
    'test-design',
  ],
  estimateMin: 35,
  skills: { 'Automation Patterns': 5, 'Test Design': 3, 'Test Documentation': 1 },
  requires: ['selenium-runner', 'pom-library'],
};

// ─── B3-POM-03 · S14 · Author · CartPage с domain helper methods ────────────

const B3_POM_03: Scenario = {
  id: 'B3-POM-03',
  sprint: 14,
  tier: 'required',
  mode: 'author',
  rank: 'automation-architect-junior',
  startRoute: '/cart',
  mountStrategy: 'iframe-route',
  title: 'CartPage · с domain helper методами',
  brief:
    'Серж: «LoginPage у тебя есть. Теперь CartPage. Не просто wrapper над DOM · а domain helpers: addItem(artworkId), removeItem(artworkId), getItemCount(), getTotalRub(), clearCart(). Тест должен читаться как описание сценария».',
  taskMd: [
    '**Контекст.** В suite уже 12 тестов · 6 из них работают с корзиной (add · remove · checkout · empty-state · etc). Каждый дублирует locator-ы корзины: `[data-testid="cart-item-row"]`, `[data-testid="cart-total"]`, `[data-testid="add-to-cart"]`. Время выделить `CartPage`.',
    '',
    '**Что делать.**',
    '1. Создай `pages/CartPage.ts` с class `CartPage`.',
    '2. Lazy getters для locator-ов:',
    '   - `itemRows` → `findElements(By.testId("cart-item-row"))` (массив!)',
    '   - `totalDisplay` → `[data-testid="cart-total"]`',
    '   - `clearCartBtn` → `[data-testid="cart-clear"]`',
    '   - `emptyStateBanner` → `[data-testid="cart-empty-state"]` (когда корзина пустая)',
    '3. Domain helper методы (не «click that button» · а «что хочет user»):',
    '   - `async navigate()` → `driver.navigate().to("/cart")`',
    '   - `async addItem(artworkId: string)` → переходит на `/listings` · находит карточку с `data-testid="artwork-card-${artworkId}"` · кликает «В корзину»',
    '   - `async removeItem(artworkId: string)` → находит row с `data-testid="cart-item-${artworkId}"` · кликает X внутри',
    '   - `async getItemCount(): Promise<number>` → возвращает `itemRows.length`',
    '   - `async getTotalRub(): Promise<number>` → читает text с `totalDisplay` · парсит в number (убирает «₽» и пробелы)',
    '   - `async clearCart()` → кликает `clearCartBtn` · ждёт пока появится `emptyStateBanner`',
    '4. В правой панели demo-тест: проверяет add → count=1 → addAnother → count=2 → getTotalRub → expected sum → clearCart → emptyState visible. Жми Run после того как POM готов.',
    '',
    '**Что важно.** **Domain language matters.** Сравни два теста:',
    '',
    '```',
    '// Без CartPage:',
    "await driver.navigate().to('/cart');",
    "const rows = await driver.findElements(By.testId('cart-item-row'));",
    'expect(rows.length).toBe(2);',
    '',
    '// С CartPage:',
    'expect(await cartPage.getItemCount()).toBe(2);',
    '```',
    '',
    'Второй вариант **читается как user-story**. Reviewer не должен знать что `cart-item-row` · `cart-total` · `cart-clear` существуют как DOM-attributes. Это **детали реализации** — они инкапсулированы в POM. Когда тест читает менеджер или новый человек в команде — он видит «getItemCount()» · понимает intent.',
    '',
    'Правило мозгового штурма: **API твоего POM = словарь действий пользователя**. Если method называется `clickCartButton()` — это red flag. Лучше `addItem(...)` · `clearCart()` · `proceedToCheckout()`.',
  ].join('\n'),
  rubric: {
    items: [
      { id: 'r1', text: 'Class `CartPage` exports · 4 lazy getters для locator-ов', weight: 1 },
      { id: 'r2', text: 'Метод `addItem(artworkId)` · navigates `/listings` · кликает по `artwork-card-${id}`', weight: 1 },
      { id: 'r3', text: 'Метод `removeItem(artworkId)` · находит row с `cart-item-${id}` · кликает X внутри', weight: 1 },
      {
        id: 'r4',
        text: 'Метод `getItemCount()` возвращает `number` · через `findElements(...).length`',
        weight: 1,
      },
      {
        id: 'r5',
        text: 'Метод `getTotalRub()` парсит text → number (убирает символы валюты и whitespace)',
        weight: 2,
      },
      {
        id: 'r6',
        text: 'Метод `clearCart()` ждёт появления emptyStateBanner после клика (explicit wait)',
        weight: 1,
      },
      { id: 'r7', text: 'Все методы названы доменным языком (addItem, не clickCartButton)', weight: 1 },
      { id: 'r8', text: 'Demo-test проходит зелёным', weight: 2 },
    ],
    passThreshold: 7,
  },
  sampleAnswer: [
    "import { driver, By, until } from '@atelier/test-runner';",
    '',
    'export class CartPage {',
    "  private get itemRows() { return driver.findElements(By.testId('cart-item-row')); }",
    "  private get totalDisplay() { return driver.findElement(By.testId('cart-total')); }",
    "  private get clearCartBtn() { return driver.findElement(By.testId('cart-clear')); }",
    "  private get emptyStateBanner() { return driver.findElement(By.testId('cart-empty-state')); }",
    '',
    "  async navigate() { await driver.navigate().to('/cart'); }",
    '',
    '  async addItem(artworkId: string) {',
    "    await driver.navigate().to('/listings');",
    '    const card = await driver.findElement(',
    '      By.testId(`artwork-card-${artworkId}`),',
    '    );',
    "    const addBtn = await card.findElement(By.testId('add-to-cart'));",
    '    await addBtn.click();',
    '  }',
    '',
    '  async removeItem(artworkId: string) {',
    '    const row = await driver.findElement(By.testId(`cart-item-${artworkId}`));',
    "    const removeBtn = await row.findElement(By.testId('cart-item-remove'));",
    '    await removeBtn.click();',
    '  }',
    '',
    '  async getItemCount(): Promise<number> {',
    '    const rows = await this.itemRows;',
    '    return rows.length;',
    '  }',
    '',
    '  async getTotalRub(): Promise<number> {',
    '    const text = await this.totalDisplay.getText();',
    "    const cleaned = text.replace(/[^0-9]/g, '');",
    '    return parseInt(cleaned, 10);',
    '  }',
    '',
    '  async clearCart() {',
    '    await this.clearCartBtn.click();',
    '    await driver.wait(',
    "      until.elementLocated(By.testId('cart-empty-state')),",
    '      3000,',
    '    );',
    '  }',
    '}',
  ].join('\n'),
  breakdown: [
    '**Senior-вариант:**',
    '',
    '1. **Cart-item itself как POM** · отдельный `CartItem` class с методами `getTitle()` · `getPriceRub()` · `remove()`. Тогда `cartPage.getItem(artworkId)` возвращает `CartItem`. Хорошо когда item имеет много действий (edit · move-to-wishlist · save-for-later). Block 3 advanced.',
    '2. **Async iterators** · `for await (const item of cartPage.items()) {...}` · элегантно но overkill для 5 items.',
    '3. **Builder pattern** · `cartPage.cart().withItem(...).withItem(...).getTotal()` · functional · но нечитаемо для junior. Skip.',
    '',
    'Твой sample-answer — solid base. Эти POMs можно reuse в S15 Playwright equivalent · S16 CI smoke-tests · и в Block 3 capstone.',
  ].join('\n'),
  scoring: { baseScore: 9, firstAttemptBonus: 1, hintPenalty: 1, maxAttempts: 3 },
  achievementTriggers: [
    'author-junior',
    'sprint-14-required',
    'automation-architect',
    'test-design',
  ],
  estimateMin: 35,
  skills: { 'Automation Patterns': 5, 'Test Design': 3 },
  requires: ['selenium-runner', 'pom-library'],
};

// ─── B3-POM-04 · S14 · Investigate · DRY violation · 2 POMs duplicate code ──

const B3_POM_04: Scenario = {
  id: 'B3-POM-04',
  sprint: 14,
  tier: 'required',
  mode: 'investigate',
  rank: 'automation-architect-junior',
  startRoute: '/cart',
  mountStrategy: 'iframe-route',
  customModule: 'block-3/pom-dry-violation.tsx',
  title: 'CartPage и CheckoutPage оба парсят total · DRY violation',
  brief:
    'Маша делала code review POMs · нашла повтор: CartPage.getTotalRub() и CheckoutPage.getOrderTotalRub() — одинаковые 4 строки парсинга text → number. Серж: «Найди дубль · кликни по одной из copy-paste строчек».',
  taskMd: [
    '**Контекст.** В POM-библиотеке два class-а: `CartPage` (только что написал в B3-POM-03) и `CheckoutPage` (написана коллегой Денисом). Оба читают «итоговую сумму» с DOM-страницы · парсят `«4 500 ₽»` → `4500`. Логика парсинга одинаковая. Это **DRY violation** — Don\'t Repeat Yourself.',
    '',
    '**Что делать.**',
    '1. В правой панели открыты оба класса side-by-side · `CartPage.ts` · `CheckoutPage.ts`. Прочитай их методы `getTotalRub()` (CartPage) и `getOrderTotalRub()` (CheckoutPage).',
    '2. Найди 4-строчный pattern который повторяется буквально one-to-one · только locator другой:',
    '   - read text from element',
    '   - replace non-digit chars',
    '   - parseInt to number',
    '   - return',
    '3. Кликни на строку в любом из двух классов · которая представляет начало этого паттерна (обычно `replace(/[^0-9]/g, "")` — её точно никто не помнит наизусть · и копируется один-в-один).',
    '',
    '**Что важно.** DRY violation — code smell, не «bug». Тесты работают · suite зелёная. Но если в будущем поменяется формат отображения цены (`«4 500.00 ₽»` с копейками · или `«$4500»` после i18n) — придётся править в **двух местах**. И обязательно одно забудешь · и suite станет flaky без видимой причины.',
    '',
    'Решения (от простого к сложному):',
    '',
    '- **Helper function** в `pages/utils.ts`: `export function parseRub(text: string): number { ... }` · оба POM импортируют. Минимум change.',
    '- **BasePage class** с protected method `_parseRub()` · CartPage/CheckoutPage extends. Хорошо когда у POMs есть и другой shared functionality.',
    '- **Mixin pattern** через TypeScript intersection types · senior · skip пока.',
    '',
    'Refactoring сразу обычно медленнее (вынеси helper · обнови оба POMs · regression risk). Но в долгую — спасает от drift.',
  ].join('\n'),
  hints: [
    {
      text: 'Сравни `CartPage.getTotalRub()` и `CheckoutPage.getOrderTotalRub()` строка-за-строкой. Какие 4 строки делают одно и то же · только locator разный?',
    },
    {
      text: 'Pattern: `text.replace(/[^0-9]/g, "")` + `parseInt(cleaned, 10)`. Эта regex-чистка повторяется в обоих POMs · точно скопирована. Это и есть DRY violation. Кликни на строку с `.replace(/[^0-9]/g, "")` в одном из POMs.',
    },
    {
      text: 'Refactor план: вынести в `pages/utils.ts` функцию `parseRub(text: string): number`. Оба POMs импортируют. Кликни на строку `text.replace(/[^0-9]/g, "")` сейчас — это начало copy-paste pattern.',
    },
  ],
  scoring: { baseScore: 6, firstAttemptBonus: 1, hintPenalty: 1, maxAttempts: 3 },
  achievementTriggers: [
    'detective-junior',
    'sprint-14-required',
    'automation-architect',
    'test-design',
  ],
  estimateMin: 18,
  skills: { 'Automation Patterns': 4, 'Test Design': 3 },
  requires: ['selenium-runner', 'pom-library'],
};

// ─── B3-POM-05 · S14 · Author · multi-page navigation flow тест ────────────

const B3_POM_05: Scenario = {
  id: 'B3-POM-05',
  sprint: 14,
  tier: 'required',
  mode: 'author',
  rank: 'automation-architect-junior',
  startRoute: '/login',
  mountStrategy: 'iframe-route',
  title: 'Multi-page flow · login → addItem → checkout · через POMs',
  brief:
    'Серж: «У нас три POM-а: LoginPage, CartPage, CheckoutPage. Напиши end-to-end тест который проходит весь happy-path: логин → добавить работу → перейти на checkout → завершить заказ → assert order-confirmation». Всё через POM-методы · никаких raw findElement в test-е.',
  taskMd: [
    '**Контекст.** Это твой первый **end-to-end test**. Раньше ты писал unit-style тесты на одну функцию (cart-add). Сейчас — flow через 3 страницы. Это и есть «testing user journey» — то что в реальности юзер делает.',
    '',
    'Doctrine: **в test-е нет raw findElement / By.cssSelector / driver.click()**. Только POM-методы. Если что-то нужно делать чего нет в POM — добавь method в соответствующий POM (но в этом тесте всё уже есть).',
    '',
    '**Что делать.**',
    '1. В editor подключены три POM-а: `LoginPage`, `CartPage`, `CheckoutPage`. Все методы что нужны:',
    '   - `loginPage.login(username, password)`',
    '   - `cartPage.addItem(artworkId)`',
    '   - `cartPage.getItemCount()` · `cartPage.getTotalRub()`',
    '   - `checkoutPage.navigate()`',
    '   - `checkoutPage.fillBillingAddress(addr)` · `checkoutPage.fillCardNumber(num)` · `checkoutPage.fillExpiry(date)` · `checkoutPage.fillCvv(code)`',
    '   - `checkoutPage.submitOrder()`',
    '   - `checkoutPage.getOrderConfirmationNumber(): Promise<string>` · `checkoutPage.getOrderTotalRub()`',
    '2. Напиши test-сценарий happy-path:',
    '   1. Login as `anya@art` / `password123`',
    '   2. Add `art_lera_014` (Vapor #14) в корзину',
    '   3. Check getItemCount === 1 · getTotalRub === 3500',
    '   4. Navigate к checkoutPage',
    '   5. Fill billing: `Москва, Тверская 5`',
    '   6. Fill card: `4242 4242 4242 4242` · expiry `12/27` · cvv `123`',
    '   7. Submit order',
    '   8. Assert order-confirmation-number начинается с `ORD-` · getOrderTotalRub === 3500',
    '3. Жми Run · флоу должен пройти весь до конца зелёным.',
    '',
    '**Что важно.** Это твой первый E2E (end-to-end) test. Несколько pattern-ов:',
    '',
    '- **Test reads as story** · readable line-by-line как пересказ user journey.',
    '- **Each step небольшой** · каждый POM-call self-contained · failure точно укажет на этап.',
    '- **Soft assertions vs hard** · в этом простом тесте hard assert (`expect`) падает immediately. В реальной production-suite иногда хочется собрать все failures (Anya дошла до checkout · но total wrong) — soft assertions. Block 3 advanced.',
    '- **Test data realistic** · Stripe test card `4242 4242 4242 4242` · реальный test artifact. На production reservation ID будет realistic тоже.',
  ].join('\n'),
  rubric: {
    items: [
      { id: 'r1', text: 'Test использует ТОЛЬКО POM-методы · ноль raw `findElement`/`By.*`', weight: 2 },
      { id: 'r2', text: 'login → addItem → checkout · все три POMs использованы', weight: 1 },
      { id: 'r3', text: 'Assertions на промежуточных шагах · не только в конце', weight: 1 },
      { id: 'r4', text: 'Order-confirmation assertion проверяет formatprefix `ORD-` (не точное значение · которое генерится)', weight: 1 },
      { id: 'r5', text: 'getOrderTotalRub равен ожидаемому 3500 · matches getTotalRub из cart', weight: 1 },
      { id: 'r6', text: 'Test data realistic (test-card 4242... · реальный test artifact)', weight: 1 },
      { id: 'r7', text: 'Test читается как user-story · можно пересказать вслух', weight: 1 },
      { id: 'r8', text: 'Test проходит зелёным end-to-end', weight: 2 },
    ],
    passThreshold: 7,
  },
  sampleAnswer: [
    "import { LoginPage } from '@atelier/pom/LoginPage';",
    "import { CartPage } from '@atelier/pom/CartPage';",
    "import { CheckoutPage } from '@atelier/pom/CheckoutPage';",
    "import { expect } from '@atelier/test-runner';",
    '',
    "test('Happy path · логин → корзина → checkout · одна работа', async () => {",
    '  const loginPage = new LoginPage();',
    '  const cartPage = new CartPage();',
    '  const checkoutPage = new CheckoutPage();',
    '',
    '  // 1. Login',
    "  await loginPage.login('anya@art', 'password123');",
    '',
    '  // 2. Add to cart',
    "  await cartPage.addItem('art_lera_014');",
    '  await cartPage.navigate();',
    '  expect(await cartPage.getItemCount()).toBe(1);',
    '  expect(await cartPage.getTotalRub()).toBe(3500);',
    '',
    '  // 3. Checkout',
    '  await checkoutPage.navigate();',
    "  await checkoutPage.fillBillingAddress('Москва, Тверская 5');",
    "  await checkoutPage.fillCardNumber('4242 4242 4242 4242');",
    "  await checkoutPage.fillExpiry('12/27');",
    "  await checkoutPage.fillCvv('123');",
    '  await checkoutPage.submitOrder();',
    '',
    '  // 4. Confirmation',
    '  const confNumber = await checkoutPage.getOrderConfirmationNumber();',
    "  expect(confNumber).toMatch(/^ORD-/);",
    '  expect(await checkoutPage.getOrderTotalRub()).toBe(3500);',
    '});',
  ].join('\n'),
  breakdown: [
    '**Senior-вариант:**',
    '',
    '1. **Test data builder** · `const order = OrderBuilder.user("anya@art").addArtwork("art_lera_014").build()` · POMs принимают builder. Чище для many-test setup.',
    '2. **Page navigation events** · `LoginPage.login(...)` возвращает `Promise<DashboardPage>` · navigation type-checked. Java `WebDriverWait` так тоже работает. Block 3 advanced.',
    '3. **Soft assertions** · `expect.soft(...)` collect-ит failures · final report показывает все 3 разом если несколько провалились. Useful для UI tests где видно сразу несколько проблем.',
    '',
    'Твой sample — junior+ E2E-test · solid · maintainable. Это пример как POM делает test читаемым как story.',
  ].join('\n'),
  scoring: { baseScore: 9, firstAttemptBonus: 1, hintPenalty: 1, maxAttempts: 3 },
  achievementTriggers: [
    'author-junior',
    'sprint-14-required',
    'automation-architect',
    'test-design',
  ],
  estimateMin: 30,
  skills: { 'Automation Patterns': 5, 'Test Design': 4 },
  requires: ['selenium-runner', 'pom-library'],
};

// ─── B3-POM-06 · S14 · Bonus · Author · refactor 5 raw тестов в POM-style ───

const B3_POM_06: Scenario = {
  id: 'B3-POM-06',
  sprint: 14,
  tier: 'bonus',
  mode: 'author',
  rank: 'automation-architect-junior',
  startRoute: '/listings',
  mountStrategy: 'iframe-route',
  title: 'Refactor · 5 raw Selenium-тестов в POM-style',
  brief:
    'Серж: «Илья прислал 5 тестов написанных in-line · ни одного POM-а · все на raw findElement. Перепиши их через нашу POM-библиотеку». Маша: «Это и есть real-world legacy refactor — каждый automation engineer этим занимается».',
  taskMd: [
    '**Контекст.** Илья (frontend) присоединился к QA team на 1 спринт чтобы помочь с automation. Написал 5 тестов на cart-flow · но без POMs (он не знал convention). Тесты работают · но maintainability нулевая · каждый дублирует locators. Твоя задача — refactor.',
    '',
    'В правой панели уже открыты:',
    '- 5 тестов Ильи (~80-100 строк суммарно)',
    '- POM-библиотека (`LoginPage` · `CartPage` · `CheckoutPage` · все из B3-POM-02..03)',
    '',
    '**Что делать.**',
    '1. Прочитай тесты Ильи. Идентифицируй raw locators · которые уже есть в POMs.',
    '2. Перепиши все 5 тестов через POM-методы. Drop-in replacement · же поведение · но читаемо.',
    '3. Добавляй methods в POMs **ТОЛЬКО ЕСЛИ** в тесте есть действие которого в POM нет (например, Илья проверял что cart показывает sale-discount badge · и в `CartPage` нет `getSaleDiscountBadge()` · тогда добавь method).',
    '4. После refactor каждый тест должен:',
    '   - Стать в 2-3 раза короче',
    '   - Не содержать raw `findElement` / `By.*` / `driver.click()`',
    '   - Читаться как user-story',
    '5. Жми Run · все 5 тестов после refactor должны пройти зелёными.',
    '',
    '**Что важно.** Refactor existing tests — это **40% работы automation engineer-а** в реальном production. Знание pattern-ов «POM как абстракция» + опыт читать legacy code · понимать intent · извлекать domain language — это core skill. Сегодня тебе дан легкий случай (5 связанных тестов · одна POM-libразрешена). Real-world бывает 50 тестов · 3 разных стиля locator-ов · 10 inconsistent helper functions · и стейкхолдеры которые торопят.',
    '',
    'Совет: refactor небольшими шагами. Сначала один тест → run → green → commit (mental). Потом следующий. Не делай all-five одновременно · если что-то сломается — debug сложнее.',
  ].join('\n'),
  rubric: {
    items: [
      { id: 'r1', text: 'Все 5 тестов работают зелёными после refactor', weight: 2 },
      {
        id: 'r2',
        text: 'Ноль raw `findElement` / `By.*` / `driver.click()` в test-bodies (только POM-method calls)',
        weight: 2,
      },
      {
        id: 'r3',
        text: 'Каждый тест после refactor короче чем оригинал (POMs уносят boilerplate)',
        weight: 1,
      },
      {
        id: 'r4',
        text: 'Если в одном из тестов было unique действие · оно добавлено как method в соответствующий POM (не in-line в test)',
        weight: 1,
      },
      {
        id: 'r5',
        text: 'Test names описательные · читается как user story («Anya adds two items, removes one, checks total»)',
        weight: 1,
      },
      { id: 'r6', text: 'Все 5 тестов используют **те же** POM-instances (не пересоздают для каждого)', weight: 1 },
    ],
    passThreshold: 6,
  },
  sampleAnswer: [
    '// Pattern для refactor одного из 5 тестов:',
    "// БЫЛО (raw):",
    "// test('add Vapor #14', async () => {",
    "//   await driver.navigate().to('/login');",
    "//   await driver.findElement(By.testId('login-username')).sendKeys('anya@art');",
    "//   await driver.findElement(By.testId('login-password')).sendKeys('password123');",
    "//   await driver.findElement(By.testId('login-submit')).click();",
    "//   await driver.navigate().to('/listings');",
    "//   const card = await driver.findElement(By.testId('artwork-card-art_lera_014'));",
    "//   await card.findElement(By.testId('add-to-cart')).click();",
    "//   await driver.navigate().to('/cart');",
    "//   const rows = await driver.findElements(By.testId('cart-item-row'));",
    '//   expect(rows.length).toBe(1);',
    '// });',
    '',
    "// СТАЛО (POM):",
    "test('Anya adds Vapor #14 · cart shows 1 item', async () => {",
    '  const loginPage = new LoginPage();',
    '  const cartPage = new CartPage();',
    '',
    "  await loginPage.login('anya@art', 'password123');",
    "  await cartPage.addItem('art_lera_014');",
    '  await cartPage.navigate();',
    '  expect(await cartPage.getItemCount()).toBe(1);',
    '});',
    '',
    '// (повторяешь pattern для оставшихся 4 тестов · добавляя в POM-ы недостающие методы если нужно)',
  ].join('\n'),
  breakdown: [
    '**Senior-вариант:** ',
    '',
    '1. **Test fixtures** · вынести `loginPage` · `cartPage` · `checkoutPage` в shared `beforeEach` setup. Тесты не пересоздают instances. Block 3 advanced.',
    '2. **Custom assertions** · `expect(cartPage).toHaveItemCount(1)` · readable но требует jest matcher extension.',
    '3. **Test categorization** · `@smoke` · `@regression` · `@flaky` tags · позволяет CI selectively run subset. S16 затронет.',
    '',
    'Твой refactor выше — это junior+ skill: legacy → maintainable POM-style suite. В реальности это и есть основная automation work для первых 6-9 месяцев.',
  ].join('\n'),
  scoring: { baseScore: 9, firstAttemptBonus: 1, hintPenalty: 1, maxAttempts: 3 },
  achievementTriggers: [
    'author-junior',
    'sprint-14-bonus',
    'automation-architect',
    'test-design',
    'curious-cat',
  ],
  estimateMin: 40,
  skills: { 'Automation Patterns': 5, 'Test Design': 4 },
  requires: ['selenium-runner', 'pom-library'],
};

// ────────────────────────────────────────────────────────────────────────────
// Sprint 15 · Playwright + trace viewer + cross-browser — B3-SEL-09..11
// ────────────────────────────────────────────────────────────────────────────

// ─── B3-SEL-09 · S15 · Investigate · trace viewer · wait-mismatch in Playwright ─

const B3_SEL_09_BUG: WaitMismatchBug = {
  id: 'B1',
  kind: 'wait-mismatch',
  hotspot: '[data-testid="search-results"]',
  actualTimeoutMs: 800,
  observedLoadMs: { min: 400, max: 1800, p95: 1500 },
  waitStrategy: 'wrong-condition',
  recommendedFix:
    'Удалить hardcoded `page.waitForTimeout(800)` · использовать Playwright auto-wait через `page.locator("[data-testid=\'search-results\']").waitFor()` ИЛИ просто `page.click("[data-testid=\'search-results\'] li:first-child")` (locator-based actions auto-wait by default · 30s timeout).',
  authorNote:
    'Маша портировала Selenium-тест в Playwright "механически" · оставила `page.waitForTimeout(800)` (это анти-паттерн в Playwright · same that Thread.sleep в Selenium). Test passes локально (search быстрый) · падает в CI 4/10 (search 1.2-1.8s p95). Playwright trace.zip показывает: pause for 800ms · then click · но search-results ещё пустой. Hotspot = search results container. Pedagogical hook: «Playwright auto-wait — feature не bug · explicit waitForTimeout = регрессия в Selenium-style mindset · доверяй locator.click() · он сам ждёт actionability».',
};

const B3_SEL_09: Scenario = {
  id: 'B3-SEL-09',
  sprint: 15,
  tier: 'required',
  mode: 'investigate',
  rank: 'automation-architect-junior',
  startRoute: '/search',
  mountStrategy: 'iframe-route',
  title: 'Playwright trace viewer · поймай race в search-flow тесте',
  brief:
    'Маша: «Портировала Selenium-тесты в Playwright. Search flaky 4 раза из 10. Открыла trace · вижу красную полоску · но не понимаю где race». Серж: «Открой trace viewer · посмотри timeline · найди шаг где waitForTimeout`».',
  taskMd: [
    '**Контекст.** Маша портировала тест с Selenium на Playwright "механически" · скопировала логику строчка-в-строчку. Один из тестов — search в листинге. Результат:',
    '',
    '- Локально (Маши macbook): 10/10 PASS · runtime ~1.2s каждый',
    '- CI (Linux runners): 6/10 PASS, 4/10 FAIL · runtime variable 1.5-3s',
    '',
    'Сообщение об ошибке в FAIL-runs: `Element is not visible · timed out 800ms waiting for element` на шаге кликнуть по первому result-у. Маша подняла PR с trace.zip из CI.',
    '',
    '**Что делать.**',
    '1. В правой панели открыт **Playwright trace viewer** · timeline шагов теста с timing (snapshot DOM перед/после каждого action).',
    '2. Прокрути timeline. Найди шаг `page.waitForTimeout(800)` · посмотри timing вокруг него:',
    '   - Шаг до: `page.fill(searchInput, "Vapor")` · отправляет поисковой запрос',
    '   - waitForTimeout 800ms — статичная пауза',
    '   - Шаг после: `page.click(firstResult)` · падает «not visible»',
    '3. В trace viewer DOM snapshot после waitForTimeout — посмотри · загружены ли search-results к этому моменту? (В FAIL-run-ах search-results container пустой · API ещё в полёте).',
    '4. В test-snippet кликни на строку `page.waitForTimeout(800)` · это и есть corrupted Selenium-style ожидание.',
    '',
    '**Что важно.** **Playwright auto-wait** — главное отличие от Selenium. Когда ты делаешь `page.click(selector)` · Playwright **сам ждёт** (до 30s по умолчанию) пока:',
    '',
    '- DOM element появится в page',
    '- Element станет visible (нет `display: none`, не покрыт другим)',
    '- Element станет stable (не двигается, animations done)',
    '- Element actionable (не disabled · не covered)',
    '',
    'Это значит **в 95% Playwright-тестов explicit wait не нужен**. Если ты пишешь `waitForTimeout` — это red flag · Selenium-style mindset · регрессия. Маша скопировала старый тест механически · и протащила анти-паттерн в новую framework. Решение: удалить `waitForTimeout` · доверить click auto-wait. Если нужен явный wait — `page.locator(selector).waitFor()` · он тоже adaptive.',
    '',
    'Trace viewer — Playwright killer-feature. Каждый failed test produces `trace.zip` с full timeline · DOM snapshots · network · console. Открываешь в `npx playwright show-trace trace.zip` (или web viewer) · видишь exactly где fail. В Selenium такое было сложно · тут free.',
  ].join('\n'),
  bugs: [B3_SEL_09_BUG],
  hints: [
    {
      text: 'В trace viewer обрати внимание на timing: между fill (поиск) и click (на result) проходит точно 800ms. Search API сам отвечает 1.2-1.5s в CI. Что произойдёт если `click` сработает до того как search-results появятся?',
    },
    {
      text: 'Анти-паттерн `page.waitForTimeout(800)` это Selenium-style hardcoded задержка. Playwright auto-wait делает её ненужной — `page.click(...)` сам ждёт actionability. Кликни на эту строку в test-snippet.',
    },
    {
      text: 'Решение: удалить `page.waitForTimeout(800)` полностью · `page.click("[data-testid=\'search-result-row\']:first-child")` сам ждёт element до 30s. Trace viewer покажет clean timeline без нелепых пауз. Кликни сейчас на строку waitForTimeout.',
    },
  ],
  scoring: { baseScore: 7, firstAttemptBonus: 1, hintPenalty: 1, maxAttempts: 3 },
  achievementTriggers: [
    'detective-junior',
    'sprint-15-required',
    'automation-architect',
  ],
  estimateMin: 22,
  skills: { 'Automation Modern': 5, 'Practice Lab': 4 },
  requires: ['playwright-runner'],
};

// ─── B3-SEL-10 · S15 · Author · write first Playwright test (cart-add) ──────

const B3_SEL_10: Scenario = {
  id: 'B3-SEL-10',
  sprint: 15,
  tier: 'required',
  mode: 'author',
  rank: 'automation-architect-junior',
  startRoute: '/listings',
  mountStrategy: 'iframe-route',
  title: 'Первый Playwright-тест · cart-add · сравни с Selenium-версией',
  brief:
    'Серж: «Портируем cart-add из Selenium в Playwright. Тот же сценарий, но с другим API. Обрати внимание на: 1) locator-методы (`page.locator(...)` вместо findElement), 2) auto-wait (нет `until.elementToBeVisible`), 3) async/await pattern». Маша: «А как assertions?» Серж: «Built-in matchers — `expect(locator).toBeVisible()`».',
  taskMd: [
    '**Контекст.** В B3-SEL-02 ты написал тест на cart-add в Selenium. Сейчас перепишешь его в Playwright · сравнишь API. Это поможет тебе быстро ориентироваться когда команда переедет с одной library на другую (а они переезжают регулярно — индустрия 2024+ сильно качнулась к Playwright).',
    '',
    '**Что делать.**',
    '1. В editor подключены: `page` (Playwright Page object · автоматически создаётся в test fixture), `expect` (Playwright matchers).',
    '2. Напиши тест который повторяет B3-SEL-02 но в Playwright API:',
    '   - Открыть `/listings`: `await page.goto("/listings")`',
    '   - Найти карточку: `page.locator("[data-testid=\'artwork-card-art_lera_014\']")`',
    '   - Внутри карточки найти кнопку «В корзину»: `card.locator("[data-testid=\'add-to-cart\']")`',
    '   - Нажать на кнопку: `await addBtn.click()` (auto-waits · НЕ нужен explicit wait)',
    '   - Дождаться toast: `const toast = page.locator("[data-testid=\'cart-toast\']")`',
    '   - Проверить text: `await expect(toast).toHaveText(/Добавлено/)` (regex matcher) ИЛИ `await expect(toast).toContainText("Добавлено")`',
    '3. Жми Run · Playwright runs против Atelier-страницы · должен пройти зелёным быстрее чем Selenium-версия (no explicit wait overhead).',
    '4. После того как тест зелёный · проверь rubric и сдай.',
    '',
    '**Что важно.** Сравнение API:',
    '',
    '| Действие | Selenium | Playwright |',
    '|---|---|---|',
    '| Find | `driver.findElement(By.testId(\'x\'))` | `page.locator(\'[data-testid="x"]\')` |',
    '| Click | `el.click()` | `loc.click()` (auto-waits) |',
    '| Type | `el.sendKeys(\'value\')` | `loc.fill(\'value\')` |',
    '| Wait | `driver.wait(until.elementVisible(...))` | (built-in · usually unnecessary) |',
    '| Assert visible | `expect(el.isDisplayed()).toBe(true)` | `await expect(loc).toBeVisible()` |',
    '| Assert text | `expect(el.getText()).toContain(\'x\')` | `await expect(loc).toContainText(\'x\')` |',
    '',
    '**Главное правило перехода:** locator (не element). В Selenium ты получаешь element-reference сразу. В Playwright `locator` — **lazy reference** · resolves только когда action вызывается. Это значит: `const x = page.locator(...)` дёшево · можно reuse, не volatile, не stale-prone.',
    '',
    'Built-in `expect(locator).toX()` matchers тоже auto-wait. Полезно: `toBeVisible()` · `toHaveText(...)` · `toHaveCount(N)` · `toBeChecked()`. Каждый retry-ит до timeout (5s по умолчанию).',
  ].join('\n'),
  rubric: {
    items: [
      { id: 'r1', text: 'Использует `page.goto(...)` вместо `driver.navigate()`', weight: 1 },
      { id: 'r2', text: 'Использует `page.locator(...)` (не `page.$(...)` deprecated · не `page.findElement`)', weight: 1 },
      { id: 'r3', text: 'Selectors через `data-testid` · `[data-testid="..."]` syntax', weight: 1 },
      { id: 'r4', text: 'НЕ использует `page.waitForTimeout(...)` (anti-pattern)', weight: 2 },
      { id: 'r5', text: 'Click через `await loc.click()` (без explicit wait перед)', weight: 1 },
      {
        id: 'r6',
        text: 'Assertion через `await expect(toast).toContainText("Добавлено")` или `toHaveText(...)` (built-in matcher)',
        weight: 2,
      },
      { id: 'r7', text: 'Тест проходит зелёным · timing разумный (быстрее Selenium-версии)', weight: 2 },
    ],
    passThreshold: 7,
  },
  sampleAnswer: [
    "import { test, expect } from '@playwright/test';",
    '',
    "test('Добавление работы в корзину показывает toast', async ({ page }) => {",
    "  await page.goto('/listings');",
    '',
    "  const card = page.locator('[data-testid=\"artwork-card-art_lera_014\"]');",
    "  const addBtn = card.locator('[data-testid=\"add-to-cart\"]');",
    '  await addBtn.click();',
    '',
    "  const toast = page.locator('[data-testid=\"cart-toast\"]');",
    "  await expect(toast).toContainText('Добавлено');",
    '});',
  ].join('\n'),
  breakdown: [
    '**Senior-вариант** (для будущих advanced lessons):',
    '',
    '1. **Test fixtures** · `test.beforeEach(async ({ page }) => { await page.goto("/listings"); })` · избегает дублирования. Playwright fixtures намного мощнее Selenium hooks.',
    '2. **Configuration** · `playwright.config.ts` задаёт baseURL · timeout · projects (chromium · firefox · webkit). Один config → multi-browser run.',
    '3. **Test recorder** · `npx playwright codegen http://localhost:3000` записывает actions как код. Useful для discovery начальных selectors. Sometimes хорош, sometimes требует чистки.',
    '4. **Soft assertions** · `expect.soft(...)` collect failures · final report shows all. Useful для UI snapshot-like checks.',
    '',
    'Твой sample выше — junior · clean Playwright test. Это foundation для следующих 30+ тестов в S15-S16.',
  ].join('\n'),
  scoring: { baseScore: 8, firstAttemptBonus: 1, hintPenalty: 1, maxAttempts: 3 },
  achievementTriggers: [
    'author-junior',
    'sprint-15-required',
    'automation-architect',
  ],
  estimateMin: 28,
  skills: { 'Automation Modern': 5, 'Test Documentation': 1 },
  requires: ['playwright-runner'],
};

// ─── B3-SEL-11 · S15 · Investigate · cross-browser quirk · Chromium vs WebKit ─

const B3_SEL_11: Scenario = {
  id: 'B3-SEL-11',
  sprint: 15,
  tier: 'bonus',
  mode: 'investigate',
  rank: 'automation-architect-junior',
  startRoute: '/profile/edit',
  mountStrategy: 'iframe-route',
  customModule: 'block-3/playwright-cross-browser-quirk.tsx',
  title: 'Cross-browser · тест зелёный в Chromium · красный в WebKit',
  brief:
    'Денис: «Тест на edit-profile зелёный в Chromium · красный в WebKit. Suite в CI проверяет оба. Не понимаю почему — selector тот же, action тот же». Серж: «Открой Playwright trace для WebKit-run · сравни DOM snapshots с Chromium».',
  taskMd: [
    '**Контекст.** В Playwright config суите задано три browser-projects: `chromium` · `firefox` · `webkit`. Тест на edit-profile (поле «телефон» · click → fill → save) проходит в Chromium и Firefox · падает в WebKit с error: `Element is not editable · input is readonly`. Selector один · action один (`page.locator(...).fill(...)`). DOM в Atelier один.',
    '',
    '**Что делать.**',
    '1. В правой панели открыты **2 trace viewers** side-by-side: Chromium PASS-run vs WebKit FAIL-run.',
    '2. Прокрути на момент `page.locator("[data-testid=\'phone-input\']").fill(...)`. Сравни DOM snapshots в обоих trace-ах.',
    '3. В Chromium snapshot input имеет `autocomplete="tel"` · но **не имеет** `readonly` attribute. Element editable.',
    '4. В WebKit snapshot тот же input имеет `readonly` attribute (true). Why? Открой DevTools на WebKit-page · посмотри HTML и computed styles input-а.',
    '5. **Hint hidden in DOM:** Frontend developer добавил трюк: `<input type="tel" inputmode="numeric" pattern="[0-9]+" readonly={isMobileSafari ? true : false}>` чтобы trigger native iOS keypad на mobile Safari. WebKit (engine for Safari) detects это и применяет readonly. На Chromium-engine browsers не triggers.',
    '6. Кликни на input «телефон» — это и есть hotspot (element которое behaves differently per browser engine).',
    '',
    '**Что важно.** **Cross-browser bugs реальны** даже в 2024+ когда «все используют Chromium». Реальные edge-cases:',
    '',
    '- **WebKit** (Safari · iOS) часто имеет stricter security · различия в form behaviour · CSS pseudo-class support (например `:focus-visible` отличается на старых versions)',
    '- **Firefox** имеет subtle differences в click-event ordering · drag-drop · printing',
    '- **Chromium** базис (Chrome · Edge · Brave) — обычно «default» behavior',
    '',
    'Решение для этого scenarios:',
    '',
    '- (А) Просить frontend убрать `readonly`-conditional · использовать другой подход к mobile keyboard (например `inputmode="tel"` без readonly)',
    '- (Б) В test добавить browser-specific branch · skip-if-webkit OR использовать `keyboard.type()` напрямую (works regardless of readonly)',
    '- (В) Force input editability через JS injection в test (last resort)',
    '',
    'Best practice — (А) · починить причину. Но если нет ownership над frontend code — (Б).',
    '',
    'В реальном production team часто имеет CI matrix `[chromium-only · cross-browser]` · cross-browser run-ится pre-release · отдельная suite. Это balance: most tests run только в chromium (fast feedback) · critical user flows в всех 3 browsers.',
  ].join('\n'),
  hints: [
    {
      text: 'Сравни DOM snapshots в обоих trace-ах · обрати внимание на attributes input-а «телефон». В одном из browser-ов есть attribute которого в другом нет.',
    },
    {
      text: 'В WebKit input имеет `readonly` attribute (Frontend код добавляет его для iOS native keypad trigger). В Chromium — нет. Playwright `fill()` не работает на readonly input. Кликни на input «телефон».',
    },
    {
      text: 'Решение: попросить frontend убрать readonly-trick (использовать `inputmode="tel"` без readonly · работает же для native keypad). Или в тесте использовать `keyboard.type()` который игнорирует readonly. Кликни на input phone — это hotspot.',
    },
  ],
  scoring: { baseScore: 7, firstAttemptBonus: 1, hintPenalty: 1, maxAttempts: 3 },
  achievementTriggers: [
    'detective-junior',
    'sprint-15-bonus',
    'automation-architect',
    'curious-cat',
  ],
  estimateMin: 25,
  skills: { 'Automation Modern': 5, 'Practice Lab': 4 },
  requires: ['playwright-runner'],
};

// ────────────────────────────────────────────────────────────────────────────
// Sprint 16 · CI/CD · GitHub Actions baseline + meta-блок · screenshot diff
// Per Genie ack 2026-05-04 Q4=D · GH Actions concrete + «что одинаково в GitLab/
// Jenkins» meta-блоки в каждом scenario для portability teaching.
// ────────────────────────────────────────────────────────────────────────────

// ─── B3-CI-01 · S16 · Investigate · YAML syntax error · pipeline не стартует ─

const B3_CI_01: Scenario = {
  id: 'B3-CI-01',
  sprint: 16,
  tier: 'required',
  mode: 'investigate',
  rank: 'ci-wrangler-junior',
  startRoute: '/ci-pipeline',
  mountStrategy: 'iframe-route',
  customModule: 'block-3/ci-yaml-syntax-error.tsx',
  title: 'GitHub Actions · workflow не стартует · YAML syntax broken',
  brief:
    'Маша запушила первый GH Actions workflow · в Actions tab GitHub-а сообщение «Invalid workflow file». Денис: «YAML строгий к whitespace · возможно где-то tab вместо spaces или missing colon». Серж: «Открой YAML lint output · он точно укажет line».',
  taskMd: [
    '**Контекст.** Маша создала первый `.github/workflows/test.yml` · push в repo. В Actions tab появилось красное сообщение: «Invalid workflow file: .github/workflows/test.yml#L8 · expected mapping value but found scalar». Workflow даже не стартует — YAML parser не может распарсить.',
    '',
    'Это типичный «pipeline не запустился» кейс · отдельный от «pipeline стартует но падает». В первом случае проблема в YAML · во втором — в команде / step-е.',
    '',
    '**Что делать.**',
    '1. В правой панели открыт YAML editor с `test.yml` (~25 строк).',
    '2. Над editor-ом показан **lint output** от GH Actions parser-а: `Line 8 · expected mapping value · found scalar`.',
    '3. Прокрути к line 8 · посмотри окружающий код. YAML строгий: `key: value` · `key:` followed by indented block · но НЕ `key value` (без двоеточия).',
    '4. Найди строку с syntax error · кликни на неё.',
    '',
    '**Что одинаково в GitLab CI / Jenkins:**',
    '',
    '- **GitLab** использует `.gitlab-ci.yml` · same YAML синтаксис · same indent/colon rules. Те же ошибки. GitLab Web UI имеет YAML lint preview.',
    '- **Jenkins** в declarative pipeline использует Groovy-based DSL (отлично от YAML) — но в *parsing* фазе те же strict-формат правила. Jenkinsfile lint в Blue Ocean или CLI `jenkins-lint`.',
    '- **CircleCI** · same YAML syntax · `.circleci/config.yml`. Web validator тоже доступен.',
    '',
    'Lesson: YAML — common denominator для большинства CI engines (kubectl · github · gitlab · azure devops). Изучая синтаксис в одном — переносится в остальные. Только Jenkins и Bamboo используют Groovy-based DSL · но они рынка теряют.',
    '',
    '**Что важно.** YAML — sensitivity points:',
    '',
    '- **Indentation** = 2 spaces (или 4) · но всегда **spaces** · не tabs. Tab-инденты — instant fail в большинстве parser-ов.',
    '- **Colon-space** · `key: value` (с пробелом) · НЕ `key:value`. Без пробела parser ругается.',
    '- **Lists** через `- item` (с дефисом и пробелом) · НЕ `-item`.',
    '- **Quotes** для special chars · `name: "release-${{ github.sha }}"` чтобы избежать interpretation `${...}` как YAML-anchor.',
    '',
    'GH Actions parser — относительно forgiving · но всё равно строг. Local lint: `actionlint .github/workflows/test.yml` (separate tool). Лучше pre-push lint hook чем CI fail.',
  ].join('\n'),
  hints: [
    {
      text: 'Lint output говорит «Line 8 · expected mapping value but found scalar». Это значит на line 8 ожидался `key: value` pair · но parser нашёл просто scalar (одиночный текст). Проверь · есть ли двоеточие после key-name?',
    },
    {
      text: 'На line 8 написано `runs-on ubuntu-latest` (без двоеточия после `runs-on`). Должно быть `runs-on: ubuntu-latest`. Это classic YAML мina — забыл двоеточие · parser сразу падает. Кликни на эту строку.',
    },
    {
      text: 'Решение: вставить `:` после `runs-on`. Кликни на line 8 (`runs-on ubuntu-latest`) сейчас — это broken syntax.',
    },
  ],
  scoring: { baseScore: 6, firstAttemptBonus: 1, hintPenalty: 1, maxAttempts: 3 },
  achievementTriggers: [
    'detective-junior',
    'sprint-16-required',
    'ci-wrangler',
  ],
  estimateMin: 18,
  skills: { 'CI/CD': 4, 'Practice Lab': 4 },
  requires: ['ci-pipeline'],
};

// ─── B3-CI-02 · S16 · Author · write first GH Actions workflow · cart tests ──

const B3_CI_02: Scenario = {
  id: 'B3-CI-02',
  sprint: 16,
  tier: 'required',
  mode: 'author',
  rank: 'ci-wrangler-junior',
  startRoute: '/ci-pipeline',
  mountStrategy: 'iframe-route',
  title: 'Напиши первый GH Actions workflow · cart-flow tests · push trigger',
  brief:
    'Серж: «Время автоматизировать запуск тестов на каждый push. GH Actions workflow · trigger на push в main · 4 шага: checkout · setup-node · npm install · npm test. Reference docs открыты в боковой панели».',
  taskMd: [
    '**Контекст.** До этого ты гонял Selenium/Playwright тесты локально (`npm test`). Сейчас делаем continuous integration · тесты автоматически на каждый push в main branch. Если кто-то pushed broken code — actions tab покажет красный X · разработчик увидит fail в течение 5 минут · починит до того как кто-то ещё подцепится.',
    '',
    '**Что делать.**',
    '1. В editor создай файл `.github/workflows/test.yml`.',
    '2. Заполни workflow:',
    '   - `name: Cart Flow Tests` (отображается в Actions tab)',
    '   - `on: push: branches: [main]` (trigger · только на push в main)',
    '   - `jobs: test:` (один job called «test»)',
    '   - `runs-on: ubuntu-latest` (Linux runner · стандарт для node-based projects)',
    '   - `steps:` (4 шага):',
    '     1. `name: Checkout · uses: actions/checkout@v4` — клонирует repo в runner',
    '     2. `name: Setup Node · uses: actions/setup-node@v4 · with: node-version: 20` — устанавливает Node 20',
    '     3. `name: Install · run: npm ci` — `npm ci` (clean install · читает package-lock · быстрее чем `npm install`)',
    '     4. `name: Run tests · run: npm test` — запускает test suite',
    '3. Жми **Run** в editor → mock-runner покажет workflow execution · 4 steps green · final «All checks passed».',
    '4. Когда зелёный — отметь rubric и сдай.',
    '',
    '**Что одинаково в GitLab CI / Jenkins:**',
    '',
    '- **GitLab CI** аналогичный workflow в `.gitlab-ci.yml`:',
    '  ```',
    '  test:',
    '    image: node:20',
    '    script:',
    '      - npm ci',
    '      - npm test',
    '  ```',
    '  Меньше boilerplate (image встроен) · но также: trigger automatic на push.',
    '- **Jenkins declarative pipeline** в `Jenkinsfile`:',
    '  ```groovy',
    '  pipeline {',
    '    agent { docker { image \'node:20\' } }',
    '    stages {',
    '      stage(\'Test\') { steps { sh \'npm ci && npm test\' } }',
    '    }',
    '  }',
    '  ```',
    '  Multi-stage pipeline structure · Groovy syntax · но logic identical: clone → install → test.',
    '',
    'Все 3 engines делают одно и то же · разница в YAML / Groovy verbosity. Pattern же: trigger · agent · steps · result.',
    '',
    '**Что важно.** Pin versions actions: `@v4` · НЕ `@latest` · НЕ `@main`. Reasons:',
    '',
    '- **Reproducibility** · если actions/checkout published v5 завтра с breaking change · твой workflow ломается без warning. v4 — фиксированный contract.',
    '- **Security** · supply chain attacks · если actions repo скомпрометирован · `@main` может тянуть malicious code. `@v4` → semver tag · less surface.',
    '- **Stability** · `@v4` это major version · все patch updates v4.x.x пройдут automatically (compatibility guaranteed semver). `@v4.1.0` — pin к specific patch.',
    '',
    'Best practice 2024+: `@v4` для major-pin · `@vN.M.K` для exact-pin sensitive workflows.',
  ].join('\n'),
  rubric: {
    items: [
      { id: 'r1', text: '`name`, `on`, `jobs` · top-level keys корректные · YAML syntax валидный', weight: 1 },
      { id: 'r2', text: '`on: push: branches: [main]` · trigger ограничен на main', weight: 1 },
      { id: 'r3', text: '`runs-on: ubuntu-latest` · стандартный Linux runner', weight: 1 },
      { id: 'r4', text: '4 шага в правильном порядке · checkout → setup-node → install → test', weight: 1 },
      { id: 'r5', text: 'Использует pinned versions actions (@v4 · не @latest · не @main)', weight: 1 },
      { id: 'r6', text: '`actions/setup-node@v4 · with: node-version: 20` · explicit Node version', weight: 1 },
      { id: 'r7', text: '`npm ci` (не `npm install`) · matches CI best practice', weight: 1 },
      { id: 'r8', text: 'Mock-runner показывает 4 green steps · workflow запускается без error', weight: 2 },
    ],
    passThreshold: 7,
  },
  sampleAnswer: [
    'name: Cart Flow Tests',
    '',
    'on:',
    '  push:',
    '    branches: [main]',
    '',
    'jobs:',
    '  test:',
    '    runs-on: ubuntu-latest',
    '    steps:',
    '      - name: Checkout',
    '        uses: actions/checkout@v4',
    '',
    '      - name: Setup Node',
    '        uses: actions/setup-node@v4',
    '        with:',
    '          node-version: 20',
    '',
    '      - name: Install',
    '        run: npm ci',
    '',
    '      - name: Run tests',
    '        run: npm test',
  ].join('\n'),
  breakdown: [
    '**Senior-вариант** добавляет:',
    '',
    '1. **Cache npm modules** · `actions/setup-node@v4` с `cache: npm` · re-уses node_modules между runs · workflow быстрее на 30-60 секунд (cold install vs cached).',
    '2. **Concurrency control** · `concurrency: group: ${{ github.ref }}, cancel-in-progress: true` · если 3 push-а подряд · только последний gonyaет · экономия CI minutes.',
    '3. **Matrix strategy** · run tests на Node 18 + 20 + 22 параллельно. Учим в B3-CI-05.',
    '4. **Status badge** в README · `![Tests](https://github.com/.../actions/workflows/test.yml/badge.svg)` · visible в repo главной странице.',
    '',
    'Твой sample-answer — junior · solid foundation. Это тот workflow с которым команда жить может месяцами.',
  ].join('\n'),
  scoring: { baseScore: 9, firstAttemptBonus: 1, hintPenalty: 1, maxAttempts: 3 },
  achievementTriggers: [
    'author-junior',
    'sprint-16-required',
    'ci-wrangler',
    'documentation',
  ],
  estimateMin: 30,
  skills: { 'CI/CD': 5, 'Test Documentation': 1 },
  requires: ['ci-pipeline'],
};

// ─── B3-CI-03 · S16 · Investigate · CI flake · env-specific timing ──────────

const B3_CI_03_BUG: WaitMismatchBug = {
  id: 'B1',
  kind: 'wait-mismatch',
  hotspot: '[data-testid="dashboard-stats"]',
  actualTimeoutMs: 5000,
  observedLoadMs: { min: 800, max: 6500, p95: 5800 },
  waitStrategy: 'explicit-too-short',
  recommendedFix:
    'Увеличить timeout с 5000ms до 10000ms ИЛИ использовать `expect(locator).toBeVisible({ timeout: 10000 })` · Playwright auto-retries с custom timeout. CI runners (shared GH Actions Linux) имеют variable performance · 5s headroom не покрывает p95.',
  authorNote:
    'Тест dashboard-load timeout 5000ms · 5/10 PASS local · 7/10 PASS CI. CI Linux runner shared · CPU/IO variable · network к API endpoint в test fixture бывает 800ms (Cache hit) · бывает 6.5s (Cache miss + cold container). p95 = 5.8s · превышает default 5s timeout. Hotspot = dashboard-stats container. Pedagogical hook: «CI runners ≠ dev machines · timing budget = f(p95-CI) · не f(p95-local) · explicit timeout overrides for CI-flaky tests».',
};

const B3_CI_03: Scenario = {
  id: 'B3-CI-03',
  sprint: 16,
  tier: 'required',
  mode: 'investigate',
  rank: 'ci-wrangler-junior',
  startRoute: '/ci-pipeline',
  mountStrategy: 'iframe-route',
  title: 'Тест зелёный локально · 3/10 fails в CI · env-specific flake',
  brief:
    'Денис: «У меня тест на dashboard pass локально 50/50. В CI 3/10 fail. Не понимаю — same code, same selectors». Серж: «CI runners shared · resource performance variable · timing-budget может быть слишком tight».',
  taskMd: [
    '**Контекст.** Тест проверяет что после login пользовательский dashboard показывает statistics widget в течение 5 секунд. Test timeout = 5000ms.',
    '',
    'Test runs:',
    '- Локально (Денис macbook · cached node_modules): 50/50 PASS · runtime ~1.5s каждый',
    '- CI (Linux ubuntu-latest · shared runner): 7/10 PASS, 3/10 FAIL · runtime variable 0.9-6.2s',
    '',
    'FAIL message: `Element not visible · timeout 5000ms exceeded`. Test code тот же · runner-side env разный.',
    '',
    '**Что делать.**',
    '1. В правой панели открыт **CI run history dashboard** · timing per run для последних 30 запусков (10 PASS local + 10 PASS CI + 10 FAIL CI).',
    '2. Сравни distribution timings:',
    '   - Local: tight cluster ~1.5s · max 1.8s',
    '   - CI: bimodal · 60% около 1.5s · 40% spread 4-6.5s · некоторые превышают 5s timeout',
    '3. Открой **GH Actions runner specs** в side panel: `ubuntu-latest` это shared 2-core/7GB Linux container · IO + network variable между runs (depends on какие jobs ещё на runner-е). Это не dev box.',
    '4. В test-snippet timeout установлен в `5000`. Когда CI runner cold + network slow → request к dashboard-stats endpoint занимает 5.8s p95.',
    '5. Кликни на dashboard-stats container в Atelier · это hotspot который иногда не успевает.',
    '',
    '**Что одинаково в GitLab CI / Jenkins:**',
    '',
    '- **GitLab CI** shared runners тоже variable · solution either увеличить timeout (`timeout: 10s` per stage) · либо использовать dedicated runners (premium).',
    '- **Jenkins** на Kubernetes-agents shared CPU · same problem. Solution: pod resource requests/limits · но это infra change.',
    '- **CircleCI** medium/large resource_class · можно купить более fast runner для critical tests.',
    '',
    'Universal lesson: **timing budgets измеряются в production CI · не на dev machine**. Если local p95 = 1.5s · CI p95 будет 1.5x-3x. Запас в timeout надо 2-3x от observed.',
    '',
    '**Что важно.** Этот scenario **четвёртый** wait-mismatch в Block 3 (после B3-SEL-03 explicit-too-short · B3-SEL-05 thread-sleep · B3-SEL-09 wrong-condition). Это означает что explicit-wait taxonomy — core skill для S13/S15/S16. Threshold ≥3 same-pattern formalization уже passed (preemptive в W3.1) · и continues paying dividend здесь.',
    '',
    'Solution patterns:',
    '',
    '- **Increase timeout** · `5000 → 10000` · safety margin против CI variance. Cheap.',
    '- **Per-action timeout** · `await locator.click({ timeout: 10000 })` · только этот шаг ждёт дольше. Granular.',
    '- **Conditional timeout** · `const TIMEOUT = process.env.CI ? 10000 : 5000` · faster locally · safer in CI. Best.',
    '- **Retry mechanism** · `retries: 1` в playwright.config.ts · automatic re-run flaky test. Backstop · не replace fix.',
  ].join('\n'),
  bugs: [B3_CI_03_BUG],
  hints: [
    {
      text: 'Local p95 = 1.8s · CI p95 = 5.8s. Timeout установлен 5000ms. В каком % CI runs тест успевает в 5000ms?',
    },
    {
      text: '60% CI runs success (1.5s среднее) · 40% — spread от 4 до 6.5 секунд · некоторые превышают 5000ms timeout. Решение — увеличить timeout до 10000ms или использовать `process.env.CI ? 10000 : 5000`. Кликни на dashboard-stats container.',
    },
    {
      text: 'Dashboard-stats container — hotspot · тот element что иногда не успевает прогрузиться за 5s в CI. Кликни сейчас на него — это правильный target.',
    },
  ],
  scoring: { baseScore: 7, firstAttemptBonus: 1, hintPenalty: 1, maxAttempts: 3 },
  achievementTriggers: [
    'detective-junior',
    'sprint-16-required',
    'ci-wrangler',
    'automation-architect',
  ],
  estimateMin: 22,
  skills: { 'CI/CD': 5, 'Practice Lab': 4 },
  requires: ['ci-pipeline'],
};

// ─── B3-CI-04 · S16 · Investigate · screenshot diff false-positive ──────────

const B3_CI_04: Scenario = {
  id: 'B3-CI-04',
  sprint: 16,
  tier: 'required',
  mode: 'investigate',
  rank: 'ci-wrangler-junior',
  startRoute: '/screenshot-diff',
  mountStrategy: 'iframe-route',
  customModule: 'block-3/screenshot-diff-false-positive.tsx',
  title: 'Visual regression · diff красный но визуально одинаково',
  brief:
    'Маша: «Visual regression suite кричит на 3 теста · diff красный · процент difference 1.2-2.5%. Открыла оба screenshot — мне кажется ОДИНАКОВО». Серж: «Откроем diff overlay · может font rendering или antialiasing разное между OS».',
  taskMd: [
    '**Контекст.** В команде есть visual regression suite — снимает screenshot ключевых страниц · сравнивает с baseline. Если pixel diff > 0.5% — fail. Maша создала baselines на macOS · CI runs на Ubuntu Linux. 3 теста сегодня показывают 1.2-2.5% diff · она открывает оба screenshot — выглядят идентично глазу.',
    '',
    '**Что делать.**',
    '1. Открой `/screenshot-diff` standalone module в Atelier · там два screenshot-а: baseline (создан на macOS) и current (created в CI Ubuntu). Diff overlay показывает где pixel differ.',
    '2. Кликни на **diff overlay toggle** · посмотри где именно красные pixels.',
    '3. Заметишь: красные pixels вокруг **text edges** — буквы, заголовки, кнопки label-ы. Body text · button labels · form labels — везде где есть rendered text.',
    '4. Background colors · graphics · images · SVG icons — все идентичны. Только text edges differ.',
    '5. Это **font rendering antialiasing** difference между macOS Quartz и Linux fontconfig. Same fonts (Inter · system-ui) · same sizes · но aliasing algorithm чуть разный · pixel-level различия в anti-aliased contours.',
    '6. Кликни на любую буквенную надпись на screenshot-е (например «Корзина» в header) · это hotspot — место где появляется false-positive diff.',
    '',
    '**Что одинаково в GitLab CI / Jenkins:**',
    '',
    '- Любой visual regression suite в shared CI runners сталкивается с этой проблемой когда baselines created на одной OS · CI запускается на другой.',
    '- Common solutions universal across CI engines:',
    '  - Generate baselines в той же environment что CI (Docker-image для baseline + same image для CI run)',
    '  - Increase pixel-diff threshold (5-10% вместо 0.5%) — но это ослабляет detection',
    '  - Use `pixelmatch` с `antialias: true` flag (игнорирует antialiasing variations)',
    '  - Use Percy / Applitools (cloud services) с smarter visual comparison',
    '',
    '**Что важно.** Visual regression — мощный tool но требует **environment parity**. Frequent причины false-positive:',
    '',
    '- **OS font rendering** · macOS vs Linux vs Windows — все рендерят font differently',
    '- **Browser engine differences** · Chromium vs Firefox vs WebKit — rendering engines varied',
    '- **DPI / device pixel ratio** · retina vs standard · 2x vs 1x screenshot',
    '- **Animation timing** · screenshot taken mid-animation gives different pixel state',
    '',
    'Best practice setup:',
    '',
    '- Baselines generated в **container** (Docker image) · same image runs в CI',
    '- `playwright.config.ts` `expect: { toMatchSnapshot: { maxDiffPixels: 100 } }` — allow few pixels diff',
    '- Specific masks для time-sensitive areas (clock · «5 minutes ago» text · etc)',
    '',
    'В этом scenario решение — **generate baselines в Linux Docker container · не на macOS**. Maша должна re-record baselines из CI первый раз · затем CI runs comparing к those.',
  ].join('\n'),
  hints: [
    {
      text: 'Открой diff overlay · посмотри где красные pixels · все ли элементы помечены или только text? Background и SVG-icons различаются?',
    },
    {
      text: 'Различия только вокруг text edges (anti-aliasing). Background · SVG · images — идентичны. Это classic OS font rendering issue (macOS vs Linux). Кликни на любую text-надпись.',
    },
    {
      text: 'Решение: re-record baselines в Linux Docker container что соответствует CI environment. Кликни на text «Корзина» в screenshot-е · это hotspot · место false-positive diff из-за antialiasing.',
    },
  ],
  scoring: { baseScore: 6, firstAttemptBonus: 1, hintPenalty: 1, maxAttempts: 3 },
  achievementTriggers: [
    'detective-junior',
    'sprint-16-required',
    'ci-wrangler',
  ],
  estimateMin: 20,
  skills: { 'CI/CD': 4, 'Practice Lab': 4 },
  requires: ['screenshot-diff', 'ci-pipeline'],
};

// ─── B3-CI-05 · S16 · Author · matrix strategy · multi-browser parallel ─────

const B3_CI_05: Scenario = {
  id: 'B3-CI-05',
  sprint: 16,
  tier: 'required',
  mode: 'author',
  rank: 'ci-wrangler-junior',
  startRoute: '/ci-pipeline',
  mountStrategy: 'iframe-route',
  title: 'GitHub Actions · matrix · run на chromium + firefox + webkit параллельно',
  brief:
    'Серж: «Хочу запускать каждый PR на 3 browsers parallel · если cross-browser regression — increase confidence до merge. GH Actions matrix strategy · короткий и elegant способ». Маша: «Это runs 3x suite · 3x времени?» Серж: «Параллельно · same wall-clock что 1 browser».',
  taskMd: [
    '**Контекст.** В B3-CI-02 ты написал workflow что запускает test suite на одном browser (default Playwright = chromium). Сейчас расширяем — running на 3 browsers · параллельно · merge gate.',
    '',
    '**Что делать.**',
    '1. В editor открыт твой workflow из B3-CI-02. Расширишь его с matrix strategy.',
    '2. Добавь `strategy: matrix:` под `jobs.test:`:',
    '   ```yaml',
    '   strategy:',
    '     matrix:',
    '       browser: [chromium, firefox, webkit]',
    '     fail-fast: false',
    '   ```',
    '   `fail-fast: false` — если chromium fail · НЕ останавливает firefox/webkit. Получаем full feedback.',
    '3. В step `Run tests` использует matrix variable: `npm run test -- --browser=${{ matrix.browser }}` (Playwright CLI flag для browser-target).',
    '4. Optional · добавь `name: Tests · ${{ matrix.browser }}` к каждому job-у — labels в GH Actions UI clearer.',
    '5. Жми **Run** · mock-runner показывает 3 parallel jobs (chromium · firefox · webkit) · все должны быть green.',
    '6. Когда все 3 зелёные — отметь rubric и сдай.',
    '',
    '**Что одинаково в GitLab CI / Jenkins:**',
    '',
    '- **GitLab CI** matrix через `parallel: matrix:`:',
    '  ```yaml',
    '  test:',
    '    parallel:',
    '      matrix:',
    '        - BROWSER: [chromium, firefox, webkit]',
    '    script:',
    '      - npm test -- --browser=$BROWSER',
    '  ```',
    '',
    '- **Jenkins** matrix через `axes`:',
    '  ```groovy',
    '  matrix {',
    '    axes {',
    '      axis { name \'BROWSER\'; values \'chromium\', \'firefox\', \'webkit\' }',
    '    }',
    '    stages { stage(\'Test\') { steps { sh "npm test --browser=$BROWSER" } } }',
    '  }',
    '  ```',
    '',
    'Все 3 engines имеют matrix concept · syntax differs · pattern identical.',
    '',
    '**Что важно.** Matrix strategy решает три проблемы:',
    '',
    '1. **Parallel execution** · 3 browsers запускаются одновременно · wall-clock time = max(chromium · firefox · webkit) · не sum',
    '2. **Independent results** · если chromium green но webkit red — видно exactly где regression. `fail-fast: false` mandatory для full feedback',
    '3. **DRY config** · один `strategy.matrix` блок · не 3 копии job',
    '',
    'Beyond browsers · matrix используется для:',
    '',
    '- **Multiple Node versions** · `node: [18, 20, 22]` · ensure compat',
    '- **Multiple OS** · `os: [ubuntu-latest, macos-latest, windows-latest]` · cross-platform check',
    '- **Multiple test suites** · `suite: [unit, integration, e2e]`',
    '- **Combinations** · `os × node × browser` = 27 jobs (бывает overkill · обычно используют subset)',
    '',
    'Cost note: matrix runs charge CI minutes per job. 3 browsers = 3x charges. Для startup teams часто компромисс: `chromium` per push · `chromium + firefox + webkit` только pre-release.',
  ].join('\n'),
  rubric: {
    items: [
      { id: 'r1', text: 'Workflow имеет `strategy: matrix:` под jobs.test', weight: 1 },
      { id: 'r2', text: 'Matrix `browser: [chromium, firefox, webkit]` · 3 values', weight: 1 },
      { id: 'r3', text: '`fail-fast: false` · ensure full feedback от всех 3 jobs', weight: 1 },
      {
        id: 'r4',
        text: 'Step `Run tests` использует `${{ matrix.browser }}` · НЕ hardcoded browser',
        weight: 2,
      },
      {
        id: 'r5',
        text: 'Optional · `name:` step или job включает `${{ matrix.browser }}` для readable UI',
        weight: 1,
      },
      { id: 'r6', text: 'Mock-runner показывает 3 parallel jobs (chromium · firefox · webkit)', weight: 2 },
      { id: 'r7', text: 'Все 3 jobs зелёные · workflow PASSED', weight: 2 },
    ],
    passThreshold: 7,
  },
  sampleAnswer: [
    'name: Cart Flow Tests · multi-browser',
    '',
    'on:',
    '  push:',
    '    branches: [main]',
    '',
    'jobs:',
    '  test:',
    '    runs-on: ubuntu-latest',
    '    strategy:',
    '      matrix:',
    '        browser: [chromium, firefox, webkit]',
    '      fail-fast: false',
    '    name: Tests · ${{ matrix.browser }}',
    '    steps:',
    '      - name: Checkout',
    '        uses: actions/checkout@v4',
    '',
    '      - name: Setup Node',
    '        uses: actions/setup-node@v4',
    '        with:',
    '          node-version: 20',
    '          cache: npm',
    '',
    '      - name: Install',
    '        run: npm ci',
    '',
    '      - name: Install Playwright browsers',
    "        run: npx playwright install ${{ matrix.browser }} --with-deps",
    '',
    '      - name: Run tests',
    "        run: npm run test -- --project=${{ matrix.browser }}",
    '',
    '      - name: Upload trace on failure',
    '        if: failure()',
    '        uses: actions/upload-artifact@v4',
    '        with:',
    '          name: playwright-trace-${{ matrix.browser }}',
    '          path: playwright-report/',
    '          retention-days: 7',
  ].join('\n'),
  breakdown: [
    '**Senior-вариант** добавляет:',
    '',
    '1. **Sharding** · split test suite на N chunks · 3 browsers × 4 shards = 12 parallel jobs · wall-clock в 4x faster чем sequential. `--shard=1/4` · `--shard=2/4` · etc.',
    '2. **Conditional skip** · skip webkit на Windows runners (no support) · `if: matrix.os != \'windows-latest\' || matrix.browser != \'webkit\'`',
    '3. **Custom job naming** · `name: ${{ matrix.os }} · ${{ matrix.browser }} · ${{ matrix.shard }}` · читаемее в Actions tab',
    '4. **Cache Playwright browsers** · `actions/cache@v4` для `~/.cache/ms-playwright` · экономит 30-60s на cold install',
    '',
    'Твой sample-answer — junior+ matrix workflow. Production-ready для большинства projects.',
  ].join('\n'),
  scoring: { baseScore: 9, firstAttemptBonus: 1, hintPenalty: 1, maxAttempts: 3 },
  achievementTriggers: [
    'author-junior',
    'sprint-16-required',
    'ci-wrangler',
  ],
  estimateMin: 35,
  skills: { 'CI/CD': 5, 'Test Design': 2 },
  requires: ['ci-pipeline'],
};

// ─── B3-CI-06 · S16 · Bonus · Author · full pipeline для Block 3 capstone ───

const B3_CI_06: Scenario = {
  id: 'B3-CI-06',
  sprint: 16,
  tier: 'bonus',
  mode: 'author',
  rank: 'ci-wrangler-junior',
  startRoute: '/ci-pipeline',
  mountStrategy: 'iframe-route',
  title: 'Full pipeline · checkout → install → matrix tests → reports → notify',
  brief:
    'Серж: «Время финального workflow для Block 3 capstone. Делаем production-grade · все best practices · checkout · cache · matrix · timeout · trace upload · Slack notify on failure. 5 jobs · ~80 строк YAML».',
  taskMd: [
    '**Контекст.** В B3-CI-02 ты написал базовый workflow (4 шага · 1 browser). В B3-CI-05 добавил matrix. Сейчас собираешь **production-grade pipeline** который реально в команде используется. Это финальный exercise S16 · применяешь всё что знаешь.',
    '',
    'Pipeline должен быть:',
    '',
    '- **Multi-browser** · chromium + firefox + webkit (matrix)',
    '- **Cached** · npm ci быстрее с cache · Playwright browsers cached',
    '- **Timeout-safe** · timeout-minutes на job · не повисает navсегда',
    '- **Trace artifacts on failure** · debug data uploaded · 7 days retention',
    '- **Slack notify** · если failure on main · pings #ci-alerts channel · использует `slackapi/slack-github-action@v1`',
    '- **Status badge ready** · workflow_dispatch trigger тоже · позволяет manual run from UI',
    '',
    '**Что делать.**',
    '1. В editor открыт skeleton с stub (commented sections для каждого блока).',
    '2. Заполни workflow:',
    '   - `name: Atelier Capstone Pipeline`',
    '   - `on: push: branches: [main, develop] · workflow_dispatch:` (manual trigger тоже)',
    '   - `jobs.test`:',
    '     - `runs-on: ubuntu-latest`',
    '     - `timeout-minutes: 20` (jobs не зависают)',
    '     - `strategy.matrix.browser: [chromium, firefox, webkit]`',
    '     - `strategy.fail-fast: false`',
    '     - 6 steps:',
    '       1. Checkout (`actions/checkout@v4`)',
    '       2. Setup Node 20 · с `cache: npm`',
    '       3. Install (`npm ci`)',
    '       4. Cache Playwright (`actions/cache@v4` для `~/.cache/ms-playwright`)',
    '       5. Install Playwright browsers (`npx playwright install ${{ matrix.browser }} --with-deps`)',
    '       6. Run tests (`npm run test -- --project=${{ matrix.browser }}`)',
    '       7. Upload trace on failure (`if: failure()` · `actions/upload-artifact@v4`)',
    '   - `jobs.notify`:',
    '     - `runs-on: ubuntu-latest`',
    '     - `needs: test` (depends on test job)',
    '     - `if: failure() && github.ref == \'refs/heads/main\'` (только если test failed AND on main)',
    '     - 1 step: `slackapi/slack-github-action@v1` · посылает message в `#ci-alerts`',
    '3. Жми Run · mock-runner показывает 3 parallel test jobs + 1 conditional notify job (skipped if все green).',
    '4. После все green — сдавай.',
    '',
    '**Что одинаково в GitLab CI / Jenkins:**',
    '',
    'Все 3 engines поддерживают этот pattern:',
    '',
    '- **Multi-stage parallel** · GitLab CI `stages: [test, notify]` + `needs: [test]`. Jenkins `stages` + `parallel`',
    '- **Conditional notify** · `rules:` (GitLab) · `when:` expressions (Jenkins)',
    '- **Artifacts** · `artifacts.paths` (GitLab) · `archiveArtifacts` (Jenkins)',
    '- **Cache** · `cache.key` + `cache.paths` (GitLab) · Jenkins build-cache plugin',
    '',
    'Pattern same · YAML/Groovy verbosity differs.',
    '',
    '**Что важно.** Этот workflow это **template для real-world**. Несколько production-secrets:',
    '',
    '- **`timeout-minutes: 20`** · если test зависает (deadlock · infinite loop · Playwright browser hung) · job kill через 20 minutes · не сжирает CI minutes навсегда',
    '- **`if: failure()` для cleanup/notify steps** · run только когда что-то сломалось · экономит channel noise',
    '- **`needs: test`** для notify · ensure test finishes first · правильный execution order',
    '- **Slack notify только on `main` branch** · feature-branch failures noisy · main breakage важнее notify',
    '- **Trace upload retention 7 days** · long enough debug · short enough cleanup (storage charges)',
    '',
    'После этого scenario · ты готов написать CI pipeline для real production team. Это Block 3 capstone foundation.',
  ].join('\n'),
  rubric: {
    items: [
      { id: 'r1', text: '`on: push:` + `workflow_dispatch:` (manual trigger через GH UI)', weight: 1 },
      { id: 'r2', text: '`timeout-minutes: 20` на test job · prevents infinite hang', weight: 1 },
      { id: 'r3', text: 'Matrix strategy `browser: [chromium, firefox, webkit]` + `fail-fast: false`', weight: 1 },
      {
        id: 'r4',
        text: 'Cache npm modules (`cache: npm` в setup-node) AND Playwright browsers (`actions/cache@v4`)',
        weight: 2,
      },
      {
        id: 'r5',
        text: 'Upload trace on failure · `if: failure()` · 7 days retention',
        weight: 1,
      },
      {
        id: 'r6',
        text: 'Notify job · `needs: test` + `if: failure() && github.ref == main` · proper conditional',
        weight: 2,
      },
      { id: 'r7', text: 'Slack action использует `slackapi/slack-github-action@v1` (pinned major)', weight: 1 },
      { id: 'r8', text: 'Mock-runner показывает 3 parallel test jobs + notify job (skipped if all green)', weight: 2 },
      { id: 'r9', text: 'YAML syntactically valid · workflow loads без errors', weight: 1 },
    ],
    passThreshold: 8,
  },
  sampleAnswer: [
    'name: Atelier Capstone Pipeline',
    '',
    'on:',
    '  push:',
    '    branches: [main, develop]',
    '  workflow_dispatch:',
    '',
    'jobs:',
    '  test:',
    '    runs-on: ubuntu-latest',
    '    timeout-minutes: 20',
    '    strategy:',
    '      matrix:',
    '        browser: [chromium, firefox, webkit]',
    '      fail-fast: false',
    '    name: Tests · ${{ matrix.browser }}',
    '    steps:',
    '      - name: Checkout',
    '        uses: actions/checkout@v4',
    '',
    '      - name: Setup Node',
    '        uses: actions/setup-node@v4',
    '        with:',
    '          node-version: 20',
    '          cache: npm',
    '',
    '      - name: Install dependencies',
    '        run: npm ci',
    '',
    '      - name: Cache Playwright browsers',
    '        uses: actions/cache@v4',
    '        with:',
    '          path: ~/.cache/ms-playwright',
    '          key: playwright-${{ runner.os }}-${{ matrix.browser }}-${{ hashFiles(\'package-lock.json\') }}',
    '',
    '      - name: Install Playwright browsers',
    '        run: npx playwright install ${{ matrix.browser }} --with-deps',
    '',
    '      - name: Run tests',
    '        run: npm run test -- --project=${{ matrix.browser }}',
    '',
    '      - name: Upload trace on failure',
    '        if: failure()',
    '        uses: actions/upload-artifact@v4',
    '        with:',
    '          name: playwright-trace-${{ matrix.browser }}',
    '          path: playwright-report/',
    '          retention-days: 7',
    '',
    '  notify:',
    '    runs-on: ubuntu-latest',
    '    needs: test',
    "    if: failure() && github.ref == 'refs/heads/main'",
    '    steps:',
    '      - name: Slack notify',
    '        uses: slackapi/slack-github-action@v1',
    '        with:',
    "          channel-id: '#ci-alerts'",
    "          slack-message: 'Atelier pipeline failed on main · ${{ github.sha }}'",
    '        env:',
    '          SLACK_BOT_TOKEN: ${{ secrets.SLACK_BOT_TOKEN }}',
  ].join('\n'),
  breakdown: [
    '**Senior-вариант** добавляет ещё 4-5 практик:',
    '',
    '1. **Concurrency control** · `concurrency: group: ${{ github.ref }}, cancel-in-progress: true` · если 5 push-ов подряд · только last gony · экономия CI minutes',
    '2. **PR comment with results** · post test summary в PR comment · reviewer видит без открытия Actions tab',
    '3. **Junit / SARIF report upload** · GitHub native test viewer показывает results',
    '4. **Sharding** · split tests на 4 chunks · 3 browsers × 4 shards = 12 parallel · wall-clock 4x faster',
    '5. **Reusable workflows** · вынести test logic в `.github/workflows/_test.yml` reusable workflow · main workflow callable',
    '',
    'Твой workflow — production-grade junior+. Это уровень который соответствует CI Wrangler Junior badge (3+ scenarios in S16) · готовность к real team CI ownership.',
  ].join('\n'),
  scoring: { baseScore: 10, firstAttemptBonus: 1, hintPenalty: 1, maxAttempts: 3 },
  achievementTriggers: [
    'author-junior',
    'sprint-16-bonus',
    'ci-wrangler',
    'documentation',
    'curious-cat',
  ],
  estimateMin: 45,
  skills: { 'CI/CD': 5, 'Test Documentation': 2 },
  requires: ['ci-pipeline'],
};

// ────────────────────────────────────────────────────────────────────────────
// BLOCK_3_SCENARIOS export · concatenated by sprint
// ────────────────────────────────────────────────────────────────────────────

export const BLOCK_3_SCENARIOS: ReadonlyArray<Scenario> = [
  // S13 · Selenium WebDriver basics — 8 scenarios (6R + 2B)
  B3_SEL_01,
  B3_SEL_02,
  B3_SEL_03,
  B3_SEL_04,
  B3_SEL_05,
  B3_SEL_06,
  B3_SEL_07,
  B3_SEL_08,
  // S14 · POM patterns — 6 scenarios (5R + 1B)
  B3_POM_01,
  B3_POM_02,
  B3_POM_03,
  B3_POM_04,
  B3_POM_05,
  B3_POM_06,
  // S15 · Playwright + trace viewer + cross-browser — 3 scenarios (2R + 1B)
  B3_SEL_09,
  B3_SEL_10,
  B3_SEL_11,
  // S16 · CI/CD · GitHub Actions baseline — 6 scenarios (5R + 1B)
  B3_CI_01,
  B3_CI_02,
  B3_CI_03,
  B3_CI_04,
  B3_CI_05,
  B3_CI_06,
];
